import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { tasksApi, settingsApi, taskStatusesApi, customFieldsApi } from '../services/api';
import { toast } from 'sonner';
import { Checkbox } from './ui/checkbox';
import { useDateTime } from '../contexts/DateTimeContext';
import { useUndo } from '../contexts/UndoContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { useTask } from '../contexts/TaskContext';
import UnreadBadge from './unread/UnreadBadge';
import {
  useUnreadsQuery, useMyBotsQuery, useTasksQuery, useLabelsQuery, useTaskTypesQuery, useTeamsQuery,
} from '../hooks/queries';
import { useAssignTaskBot } from '../hooks/mutations';
import BotAvatar from './bots/BotAvatar';
import { useBotLabel } from './bots/botDisplay';
import BotName from './bots/BotName';
import { useAuth } from '../contexts/AuthContext';
import InlineTaskRow from './InlineTaskRow';
import ColumnSelector from './ColumnSelector';
import TableFooter from './TableFooter';
import TimeSelect from './ui/TimeSelect';
import AddFieldModal from './AddFieldModal';
import { useEntitlements } from '../hooks/useEntitlements';
import { useMediaQuery, SCREEN } from '../hooks/useMediaQuery';
import ProjectList from './bots/ProjectList';
import ConfirmModal from './ConfirmModal';
import CustomFieldCell from './CustomFieldCell';
import ProjectChip from './ProjectChip';
import {
  Plus, Search, Filter, ChevronDown, ChevronRight,
  CheckCircle2, Circle, Clock, Calendar, X, Folder,
  ArrowUpDown, ArrowUp, ArrowDown, Eye, EyeOff, Columns3, GripVertical, Lock,
  CheckSquare, Sparkles, TrendingUp, Bug, Wrench, Receipt, Tags, FileText, User, Check,
  MoreHorizontal, Pencil, Trash2, Pin, Paperclip
} from 'lucide-react';
import { PRIORITIES, DEFAULT_STATUSES, TASK_TYPE_ICONS as taskTypeIcons, extractPlainText } from '../lib/constants';
import { operatorLabel, evaluateCondition as evaluateTaskCondition, NO_PROJECT, shownProjectId } from '../lib/taskFilterEngine';
import { statusName, statusValueName } from '../lib/statusName';
import { nestTasks, subtaskProgress } from '../lib/subtasks';
import { taskKeys, labelKeys, customStatusKeys, customFieldKeys, settingsKeys } from '../lib/queryKeys';
import { EMPTY_ARRAY } from '../lib/empty';
import DatePicker from './task/pickers/DatePicker';
import { SpotlightMark, useSpotlightMarks } from './spotlight/SpotlightMenu';

// Column definitions (labels are filled in inside the component via useMemo)
const COLUMN_DEFINITIONS_BASE = [
  { key: 'title',     required: true,  span: 4, defaultVisible: true },
  { key: 'status',    required: false, span: 2, defaultVisible: true },
  { key: 'priority',  required: false, span: 2, defaultVisible: true },
  { key: 'due_date',  required: false, span: 2, defaultVisible: true },
  { key: 'duration',  required: false, span: 2, defaultVisible: true },
  // Extra columns (hidden by default - user opts in)
  { key: 'project',   required: false, span: 2, defaultVisible: false },
  { key: 'labels',    required: false, span: 2, defaultVisible: false },
  { key: 'scheduled', required: false, span: 2, defaultVisible: true },
  { key: 'task_type', required: false, span: 2, defaultVisible: false },
  { key: 'assignee',  required: false, span: 2, defaultVisible: true },
  { key: 'owner',     required: false, span: 2, defaultVisible: false },
  { key: 'team',      required: false, span: 2, defaultVisible: false },
  { key: 'created_at', required: false, span: 2, defaultVisible: false },
];

// Static cell class lookups (hardcoded so Tailwind JIT includes them)
const CELL_WRAPPER_CLASS = {
  title:     'col-span-12 sm:col-span-4 flex items-center gap-3',
  status:    'hidden sm:flex col-span-2 items-center',
  priority:  'hidden sm:flex col-span-2 items-center',
  due_date:  'hidden sm:flex col-span-2 items-center',
  duration:  'hidden sm:flex col-span-2 items-center',
  project:   'hidden sm:flex col-span-2 items-center',
  labels:    'hidden sm:flex col-span-2 items-center gap-1 flex-wrap min-w-0',
  scheduled: 'hidden sm:flex col-span-2 items-center',
  task_type: 'hidden sm:flex col-span-2 items-center',
  assignee:   'hidden sm:flex col-span-2 items-center gap-1',
  owner:      'hidden sm:flex col-span-2 items-center gap-1',
  team:       'hidden sm:flex col-span-2 items-center',
  created_at: 'hidden sm:flex col-span-2 items-center',
};

// What a cell that opens an editor shows: one size of type (12px) and the same soft
// background while the pointer is anywhere on the cell (group/cell), in every column.
const CELL_CHIP = 'inline-flex items-center gap-1.5 min-w-0 max-w-full rounded-md px-1.5 py-1 -mx-1.5 text-xs transition-colors group-hover/cell:bg-black/[0.06] dark:group-hover/cell:bg-white/[0.10]';

// Backward-compat alias: TasksTable uses .color for bgColor
const priorityConfig = Object.fromEntries(
  Object.entries(PRIORITIES).map(([k, v]) => [k, { ...v, color: v.bgColor, label: v.altLabel }])
);

// The done circle: only urgent and high priority keep a color (the priority
// column already shows the level).
const circleTone = (p) => (p === 'p1' ? 'text-red-500' : p === 'p2' ? 'text-orange-500' : 'text-muted-foreground/60');

// The task being dragged. Shared by every table on the page (a task can be dropped into
// another list), and needed while dragging, when the browser hides the drag data.
let draggingTaskId = null;

// Optimistic cell values shown until the server confirms them: { [taskId]: { [field]: value } }.
const withPending = (taskId, field, value) => (prev) => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), [field]: value } });
const withoutPending = (taskId, field) => (prev) => {
  if (!prev[taskId] || !(field in prev[taskId])) return prev;
  const { [field]: _dropped, ...rest } = prev[taskId];
  const next = { ...prev };
  if (Object.keys(rest).length) next[taskId] = rest;
  else delete next[taskId];
  return next;
};

const statusConfig = Object.fromEntries(
  Object.entries(DEFAULT_STATUSES).map(([k, v]) => [k, { ...v, color: v.bgColor }])
);

// Legacy task.status enum → custom-status category. Used to canonicalize a
// task's status when grouping, so the same logical status across different
// members/teams (each of which has its own status_id) collapses into one group.
const LEGACY_TO_CATEGORY = {
  todo: 'not_started', in_progress: 'in_progress', completed: 'completed', archived: 'archived',
};

// ─── Dynamic Filter System ────────────────────────────────────────────────────

const FIELD_OPERATORS = {
  text:         ['contains', 'not_contains', 'is_set', 'is_not_set'],
  url:          ['contains', 'not_contains', 'is_set', 'is_not_set'],
  number:       ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_set', 'is_not_set'],
  select:       ['eq', 'neq', 'is_set', 'is_not_set'],
  multi_select: ['contains', 'not_contains', 'is_set', 'is_not_set'],
  checkbox:     ['is_true', 'is_false'],
  date:         ['on', 'before', 'after', 'on_or_before', 'on_or_after', 'is_today', 'is_this_week', 'is_overdue', 'is_set', 'is_not_set'],
};

const NO_VALUE_OPERATORS = new Set(['is_set', 'is_not_set', 'is_true', 'is_false', 'is_today', 'is_this_week', 'is_overdue']);

// Dynamic "current user" value for assignee/owner filters. Resolved to the
// logged-in user's id at evaluation time, so saved filters and presets keep
// working per-user (Linear/ClickUp-style "Me" token).
const ME_VALUE = '__me__';

// A filter condition only constrains results once it's "complete": no-value
// operators (is_set, is_true…) are always meaningful; value operators need a
// non-empty value. Opening the filter panel auto-adds a blank condition, which
// must NOT count as an active filter (button highlight) nor filter any tasks.
const conditionIsActive = (c) =>
  !!c && (
    NO_VALUE_OPERATORS.has(c.operator) ||
    (c.value != null && c.value !== '' && !(Array.isArray(c.value) && c.value.length === 0))
  );

const styledInput = 'bg-accent/60 border border-border rounded-lg text-foreground text-sm px-3 py-1.5 focus:outline-none focus:border-amber-500/50 transition-colors placeholder:text-muted-foreground/70 w-36 max-md:w-full max-md:flex-1 max-md:min-w-[7.5rem]';

const FilterSelect = ({ value, onChange, options, placeholder, className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const selected = options.find(o => String(o.value) === String(value));

  return (
    <div ref={ref} className={`relative flex-shrink-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center justify-between gap-2 w-full bg-accent/60 border border-border rounded-lg text-sm px-3 py-1.5 hover:border-input transition-colors"
      >
        <span className={`truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>
          {selected?.label ?? placeholder ?? t('ui_views.filters.select')}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 min-w-full bg-popover border border-border rounded-lg shadow-xl py-1 max-h-52 overflow-y-auto">
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors hover:bg-accent ${String(value) === String(o.value) ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterValueInput = ({ fieldDef, condition, onChange }) => {
  const { t } = useTranslation();
  const { operator, value } = condition;
  if (!fieldDef || NO_VALUE_OPERATORS.has(operator)) return null;

  if (fieldDef.type === 'select' || fieldDef.type === 'multi_select') {
    const opts = [{ value: '', label: t('ui_views.filters.select') }, ...(fieldDef.options || [])];
    return (
      <FilterSelect
        value={value}
        onChange={v => onChange({ ...condition, value: v })}
        options={opts}
        className="w-36 max-md:flex-1 max-md:min-w-[7.5rem]"
      />
    );
  }
  if (fieldDef.type === 'number') {
    return (
      <input type="number" value={value} onChange={e => onChange({ ...condition, value: e.target.value })}
        className={styledInput} placeholder="0" />
    );
  }
  if (fieldDef.type === 'date') {
    return (
      <input type="date" value={value} onChange={e => onChange({ ...condition, value: e.target.value })}
        className={`${styledInput} [color-scheme:dark]`} />
    );
  }
  return (
    <input type="text" value={value} onChange={e => onChange({ ...condition, value: e.target.value })}
      className={styledInput} placeholder={t('ui_views.filters.value')} />
  );
};

const FilterCondition = ({ condition, filterableFields, onChange, onRemove }) => {
  const { t } = useTranslation();
  const fieldDef = filterableFields.find(f => f.key === condition.field);
  const fieldType = fieldDef?.type ?? 'select';
  const operators = FIELD_OPERATORS[fieldType] ?? FIELD_OPERATORS.select;

  const handleFieldChange = (newField) => {
    const newDef = filterableFields.find(f => f.key === newField);
    const newType = newDef?.type ?? 'select';
    const newOps = FIELD_OPERATORS[newType] ?? FIELD_OPERATORS.select;
    onChange({ ...condition, field: newField, operator: newOps[0], value: '' });
  };

  const fieldOptions = filterableFields.map(f => ({ value: f.key, label: f.label }));
  const operatorOptions = operators.map(op => ({ value: op, label: operatorLabel(op, t) }));

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <FilterSelect value={condition.field} onChange={handleFieldChange} options={fieldOptions} className="w-36 max-md:flex-1 max-md:min-w-[7.5rem]" />
      <FilterSelect
        value={condition.operator}
        onChange={newOp => onChange({ ...condition, operator: newOp, value: '' })}
        options={operatorOptions}
        className="w-32 max-md:flex-1 max-md:min-w-[7rem]"
      />
      <FilterValueInput fieldDef={fieldDef} condition={condition} onChange={onChange} />
      <button onClick={onRemove} className="text-muted-foreground/70 hover:text-red-400 transition-colors flex-shrink-0">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

const FilterGroupRow = ({ group, filterableFields, onChangeCondition, onAddOr, onRemoveCondition, onRemoveGroup }) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-start gap-2 flex-wrap">
    <div className="flex items-start gap-2 flex-wrap flex-1 min-w-0">
      {group.conditions.map((cond, i) => (
        <React.Fragment key={cond.id}>
          {i > 0 && (
            <span className="text-xs text-amber-500 font-semibold self-center px-1 py-0.5 bg-amber-500/10 rounded">
              {t('ui_views.filters.or')}
            </span>
          )}
          <FilterCondition
            condition={cond}
            filterableFields={filterableFields}
            onChange={updated => onChangeCondition(cond.id, updated)}
            onRemove={() => onRemoveCondition(cond.id)}
          />
        </React.Fragment>
      ))}
      <button
        onClick={onAddOr}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-700 dark:hover:text-amber-400 transition-colors self-center px-1.5 py-0.5 rounded border border-dashed border-border hover:border-amber-500/50"
      >
        <Plus className="w-3 h-3" />
        {t('ui_views.filters.or')}
      </button>
    </div>
    <button
      onClick={onRemoveGroup}
      className="text-muted-foreground/70 hover:text-red-400 transition-colors flex-shrink-0 self-center mt-0.5"
      title={t('ui_views.filters.remove_group')}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────

// The status lists of several teams as one, each status once (useQueries runs it again
// only when a list changes).
const combineStatuses = (results) => {
  const combined = [];
  const seen = new Set();
  results.forEach(({ data }) => (data || []).forEach(st => { if (!seen.has(st.id)) { seen.add(st.id); combined.push(st); } }));
  return combined.length ? combined : EMPTY_ARRAY;
};

const TasksTable = ({
  tasks = [],
  projects = [],
  teams = null,
  onUpdate,
  onOptimisticUpdate,
  showProjectColumn = true,
  showSearch = true,
  showFilters = true,
  showGrouping = true,
  showAddButton: showAddButtonProp = true,
  showCompletedToggle = false,
  showColumnSelector = true,
  // A page bar (TaskFilterBar) that hosts the desktop controls in its own row, so the
  // page does not show two toolbars. undefined: the table draws its own toolbar;
  // null: the host is still mounting (nothing is drawn yet).
  toolbarTarget,
  compactMobile = false,
  defaultGroupBy = 'none',
  emptyMessage,
  emptyDescription,
  prefillProjectId = null,
  prefillSectionId = null,
  prefillDueDate = null,
  // 'manual': the list keeps the order people drag tasks into (project lists); clicking a
  // sorted column a third time returns to it. Otherwise the column sorted by default.
  defaultSortColumn = 'due_date',
  // Show the inline quick-add row at the bottom of each group. Disable for
  // contexts where adding doesn't make sense (e.g. an Overdue list).
  showInlineAdd: showInlineAddProp = true,
  // A list without tasks shows just its quick-add row instead of the big empty state
  // (a project's lists: each ends with "Add task", empty or not).
  emptyInlineAdd = false,
  tableId = 'tasks', // 'inbox', 'tasks', or 'agenda' - used for saving sort preferences
  // External column/footer control (for Agenda timeline mode)
  externalVisibleColumns = null,
  externalFooterConfig = null,
  onExternalFooterConfigChange = null,
  // Bulk select
  showBulkSelect = false,
  onSelectionChange = null,
  projectId = null,
  teamId = null,
  // A public page (PublicTeamPage): tasks to read, nothing to change or save.
  readOnly = false,
  // Drawn inside a card of its own (Home's Today and Overdue): no frame of the table's own.
  frameless = false,
  // A list with an order of its own (a Spotlight section): dragging a row (also one from another
  // such list) hands the new order of the tasks to this instead of moving them in a project.
  onManualOrder = null,
  // Told about a task made with the inline row at the bottom (a Spotlight section puts it there).
  onInlineCreated = null,
}) => {
  const botLabel = useBotLabel();
  const { t } = useTranslation();
  // Legacy status enum in the app's language (unknown values show as To Do, like statusConfig)
  const legacyStatusName = useCallback((value) => statusValueName(statusConfig[value] ? value : 'todo', t), [t]);
  const { limitOf } = useEntitlements();
  const { formatDate, toLocalDateString } = useDateTime();
  const { pushUndo } = useUndo();

  // One hook call for the whole table: rows render inside a .map(), so they
  // read from this shared snapshot rather than each calling a hook.
  const { data: unreads } = useUnreadsQuery();
  const hasUnread = useCallback((id) => Boolean(unreads?.tasks?.[id]), [unreads]);
  // The person's Spotlight: a task in it has a mark next to its title (components/spotlight).
  const spotlight = useSpotlightMarks();

  // Build translated column definitions
  const COLUMN_DEFINITIONS = useMemo(() => [
    { key: 'title',     label: t('tasks.task_column'),  required: true,  span: 4, defaultVisible: true },
    { key: 'status',    label: t('tasks.status'),    required: false, span: 2, defaultVisible: true },
    { key: 'priority',  label: t('tasks.priority'),  required: false, span: 2, defaultVisible: true },
    { key: 'due_date',  label: t('tasks.due_date'),  required: false, span: 2, defaultVisible: true },
    { key: 'duration',  label: t('tasks.duration'),  required: false, span: 2, defaultVisible: true },
    { key: 'project',   label: t('tasks.project'),   required: false, span: 2, defaultVisible: false },
    { key: 'labels',    label: t('tasks.labels'),    required: false, span: 2, defaultVisible: false },
    { key: 'scheduled', label: t('tasks.scheduled'), required: false, span: 2, defaultVisible: true },
    { key: 'task_type', label: t('tasks.type'),      required: false, span: 2, defaultVisible: false },
    { key: 'assignee',  label: t('tasks.assignee'),  required: false, span: 2, defaultVisible: true },
    { key: 'owner',     label: t('common.owner'),    required: false, span: 2, defaultVisible: false },
    { key: 'team',      label: t('common.team'),     required: false, span: 2, defaultVisible: false },
    { key: 'created_at', label: t('tasks.date_created'), required: false, span: 2, defaultVisible: false },
  ], [t]);

  // TaskContext
  const { openTaskCreation, openTaskDetail } = useTask();
  const { user } = useAuth();
  const { data: myBots } = useMyBotsQuery();
  const botsById = useMemo(
    () => Object.fromEntries((myBots?.bots || []).map((b) => [b.id, b])), [myBots]);
  const assignTaskBot = useAssignTaskBot();
  const currentUserId = user?.id || null;
  // Phones get the card rows, wider screens the grid with its header and footer; only
  // the one that is seen is drawn (hooks/useMediaQuery.js).
  const wide = useMediaQuery(SCREEN.sm);
  // The desktop toolbar (md and up); phones open a sheet. Its wrappers stay, empty, so the
  // rows below keep their spacing (space-y counts hidden siblings).
  const toolbarShown = useMediaQuery(SCREEN.md);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroups, setFilterGroups] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const presetMenuRef = useRef(null);
  useEffect(() => {
    if (!showPresetMenu) return;
    const close = (e) => { if (presetMenuRef.current && !presetMenuRef.current.contains(e.target)) setShowPresetMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showPresetMenu]);
  const [showCompleted, setShowCompleted] = useState(false);

  // Grouping state
  const [groupBy, setGroupBy] = useState(defaultGroupBy);

  // Bulk-select mode. The checkbox column (and the horizontal shift it causes)
  // is OFF by default and only appears once the user explicitly enables the
  // "Select tasks" toggle in the toolbar. `showBulkSelect` is the capability
  // flag from the parent; `bulkSelectMode` is the user toggle; `bulkActive` is
  // the two combined - the single source of truth for "render the checkbox column".
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const bulkActive = showBulkSelect && bulkSelectMode;

  // Expanded groups
  const [expanded, setExpanded] = useState({});
  // Tasks whose subtasks are folded away (lib/subtasks.js); all are open at first.
  const [collapsedParents, setCollapsedParents] = useState(() => new Set());
  const toggleSubtasks = useCallback((taskId) => {
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  }, []);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortLoaded, setSortLoaded] = useState(false);

  // Recently-created tasks (from the inline quick-add). Each id stays in
  // this set for ~1.4s so we can flash an amber highlight on its row when
  // the table re-sorts and moves the new task to its final position. The
  // animation is purely CSS - one keyframe defined in index.css. Mirrors
  // ClickUp's "where did my new task go?" feedback affordance.
  const [recentlyCreatedIds, setRecentlyCreatedIds] = useState(() => new Set());
  const handleInlineTaskCreated = useCallback((newTask) => {
    if (!newTask?.id) return;
    onInlineCreated?.(newTask);  // a Spotlight section takes the task made in it
    setRecentlyCreatedIds((prev) => {
      const next = new Set(prev);
      next.add(newTask.id);
      return next;
    });
    setTimeout(() => {
      setRecentlyCreatedIds((prev) => {
        if (!prev.has(newTask.id)) return prev;
        const next = new Set(prev);
        next.delete(newTask.id);
        return next;
      });
    }, 1500);
    onUpdate?.(newTask);
  }, [onUpdate, onInlineCreated]);

  // What the columns show, from the shared query cache: every table on a page (Home has
  // three) used to fetch all of it again on mount and draw once per answer.
  const queryClient = useQueryClient();
  // Labels data (for Labels column)
  const labels = useLabelsQuery().data ?? EMPTY_ARRAY;

  // Task types data (for Type column)
  const taskTypes = useTaskTypesQuery().data ?? EMPTY_ARRAY;

  // Users map (user_id -> member info) built from all teams (for Assignee column), and
  // the teams map (team_id -> team) for the Team column. The `teams` prop when the parent
  // has them, else the teams query.
  const teamsQuery = useTeamsQuery({ enabled: teams === null });
  const teamList = teams !== null ? teams : teamsQuery.data;
  const { usersMap, teamsMap } = useMemo(() => {
    const uMap = {};
    const tMap = {};
    // Seed the current user so a task assigned to me resolves even when I'm
    // not a member of any team (e.g. personal tasks on My Tasks). Team data
    // below overrides it if present.
    if (user?.id) {
      uMap[user.id] = { user_id: user.id, full_name: user.full_name, avatar_url: user.avatar_url, email: user.email };
    }
    (teamList || []).forEach(team => {
      tMap[team.id] = team;
      (team.members || []).forEach(m => { uMap[m.user_id] = m; });
    });
    return { usersMap: uMap, teamsMap: tMap };
  }, [teamList, user]);

  // Custom statuses from DB. On a single-team/personal table one set is right. But on
  // aggregated tables (My Tasks / Home, teamId null) the rows span every team the user
  // is in, so personal + each team's statuses are combined - otherwise a team task's
  // status_id isn't found and the cell falls back to the legacy enum, showing a
  // different status than the detail.
  const statusTeamIds = useMemo(
    () => (teamId ? [teamId] : [null, ...(Array.isArray(teams) ? teams : []).map(tm => tm.id)]),
    [teamId, teams],
  );
  const customStatuses = useQueries({
    queries: statusTeamIds.map(id => ({
      queryKey: customStatusKeys.list(id),
      queryFn: () => taskStatusesApi.getAll(id),
    })),
    combine: combineStatuses,
  });

  // Custom field definitions
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editingFieldDef, setEditingFieldDef] = useState(null);
  const [fieldMenuOpen, setFieldMenuOpen] = useState(null);
  const [confirmDeleteField, setConfirmDeleteField] = useState(null); // { id, label }

  // Column visibility state (defaults match COLUMN_DEFINITIONS_BASE.defaultVisible)
  const [internalVisibleColumns, setInternalVisibleColumns] = useState(
    Object.fromEntries(COLUMN_DEFINITIONS_BASE.map(c => [c.key, c.defaultVisible ?? true]))
  );

  // Column order state
  const [columnOrder, setColumnOrder] = useState(COLUMN_DEFINITIONS_BASE.map(c => c.key));
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragColRef = useRef(null);

  // Optimistic update overrides: taskId → partial task fields
  const [pendingUpdates, setPendingUpdates] = useState({});
  const showAddButton = showAddButtonProp && !readOnly;
  const showInlineAdd = showInlineAddProp && !readOnly;
  // Per-user table settings (columns, sort, footer, grouping); a read-only table keeps them local.
  const saveSettings = useCallback((payload) => (readOnly ? Promise.resolve() : settingsApi.update(payload)), [readOnly]);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastClickedIndexRef = useRef(null);

  // Inline cell editing
  const [editingCell, setEditingCell] = useState(null); // { taskId, columnKey, rect, task }
  const inlineDropdownRef = useRef(null);
  // The editor's size is a guess when it opens (openCellEditor); once drawn it is measured and
  // moved back inside the window, so no editor is ever cut at the right or bottom edge.
  useLayoutEffect(() => {
    const el = inlineDropdownRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.right > window.innerWidth - 8) el.style.left = `${Math.max(8, window.innerWidth - r.width - 8)}px`;
    if (r.bottom > window.innerHeight - 8 && !el.style.bottom) el.style.top = `${Math.max(8, window.innerHeight - r.height - 8)}px`;
  }, [editingCell]);

  // Inline title editing
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const titleInputRef = useRef(null);

  // A task row is draggable, so relying on the input's autoFocus alone is
  // brittle across browsers. Focus the editor after React has replaced the
  // title with the input and select the current title for immediate editing.
  useEffect(() => {
    if (!editingTitleId || !titleInputRef.current) return;
    titleInputRef.current.focus();
    titleInputRef.current.select();
  }, [editingTitleId]);

  // Footer aggregation config
  const [internalFooterConfig, setInternalFooterConfig] = useState({
    title:     { function: 'count' },
    status:    { function: 'none' },
    priority:  { function: 'none' },
    due_date:  { function: 'none' },
    duration:  { function: 'sum' },
    project:   { function: 'none' },
    labels:    { function: 'none' },
    scheduled: { function: 'none' },
    task_type: { function: 'none' },
    assignee:  { function: 'none' },
    owner:     { function: 'none' },
  });

  // Dynamic column list: built-in columns + user-defined custom field columns
  const allColumnDefs = useMemo(() => [
    ...COLUMN_DEFINITIONS,
    ...customFieldDefs.map(def => ({
      key: `cf_${def.id}`,
      label: def.name,
      required: false,
      span: 2,
      defaultVisible: true,
      isCustom: true,
      fieldDef: def,
    })),
  ], [COLUMN_DEFINITIONS, customFieldDefs]);

  // Filter config for each built-in column key (type + options).
  // Derived directly from allColumnDefs so Filters always stays in sync with Columns.
  const filterableFields = useMemo(() => {
    // "Me" first, then all known members - so "Assignee contains Me" /
    // "Owner is Me" are one click away and survive user switches.
    const userOptions = [
      { value: ME_VALUE, label: t('tasks.me') },
      ...Object.entries(usersMap).map(([uid, u]) => ({ value: uid, label: u.full_name || uid })),
    ];

    const builtInConfig = {
      title:      { type: 'text' },
      status:     { type: 'select', options: customStatuses.length > 0
          ? customStatuses.filter(s => s.category !== 'archived').map(s => ({ value: s.id, label: statusName(s, t) }))
          : Object.keys(statusConfig).map(v => ({ value: v, label: statusValueName(v, t) })) },
      priority:   { type: 'select', options: Object.entries(priorityConfig).map(([v, c]) => ({ value: v, label: t(c.altLabelKey) })) },
      due_date:   { type: 'date' },
      duration:   { type: 'number' },
      project:    { type: 'select', options: [{ value: NO_PROJECT, label: t('tasks.no_project') }, ...projects.map(p => ({ value: p.id, label: p.name }))] },
      labels:     { type: 'multi_select', options: labels.map(l => ({ value: l.id, label: l.name })) },
      scheduled:  { type: 'checkbox' },
      task_type:  { type: 'select', options: taskTypes.map(t => ({ value: t.id, label: t.name })) },
      // multi_select so "contains" matches ANY assignee, not just the first one
      assignee:   { type: 'multi_select', options: userOptions },
      owner:      { type: 'select', options: userOptions },
      team:       { type: 'select', options: Object.values(teamsMap).map(t => ({ value: t.id, label: t.name })) },
      created_at: { type: 'date' },
    };

    // Filterable fields that are not table columns
    const extraFields = [
      { key: 'description', label: t('common.description'), type: 'text' },
      { key: 'recurring',   label: t('tasks.filters.recurring'), type: 'checkbox' },
    ];

    return [...allColumnDefs.map(col => {
      if (col.isCustom) {
        return {
          key: col.key,
          label: col.label,
          type: col.fieldDef.field_type,
          options: col.fieldDef.options?.map(o => ({ value: o.id, label: o.label, color: o.color })) || [],
        };
      }
      const config = builtInConfig[col.key];
      if (!config) return null;
      return { key: col.key, label: col.label, ...config };
    }).filter(Boolean), ...extraFields];
  }, [allColumnDefs, customStatuses, projects, usersMap, teamsMap, labels, taskTypes, t]);

  // Evaluate a single filter condition against a task: the shared engine's rules, so the
  // table and the page's filter bar (lib/taskFilterEngine.js) never disagree.
  const evaluateCondition = useCallback(
    (task, condition) => evaluateTaskCondition(task, condition, { customStatuses, currentUserId, projects }),
    [customStatuses, currentUserId, projects],
  );

  // Use external or internal state
  const visibleColumns = externalVisibleColumns || internalVisibleColumns;
  const footerConfig = externalFooterConfig || internalFooterConfig;

  // Total span units of visible columns (used to decide if horizontal scroll is needed)
  const totalVisibleSpans = useMemo(() =>
    columnOrder
      .filter(key => visibleColumns[key] !== false)
      .reduce((sum, key) => {
        const col = allColumnDefs.find(c => c.key === key);
        return sum + (col?.span || 0);
      }, 0),
  [columnOrder, visibleColumns, allColumnDefs]);

  // Grid template string (prepends a 40px checkbox column when bulk-select is enabled,
  // appends 32px for the "+" add-field button in the header)
  const gridTemplate = useMemo(() => {
    const base = `repeat(${totalVisibleSpans}, minmax(0, 1fr)) 32px`;
    return bulkActive ? `40px ${base}` : base;
  }, [totalVisibleSpans, bulkActive]);

  // A label created from the task detail/creation panel appears here instead of
  // rendering "-" (its id is on the task but wasn't in the cached list).
  useEffect(() => {
    const reloadLabels = () => queryClient.invalidateQueries({ queryKey: labelKeys.all });
    window.addEventListener('labelsUpdated', reloadLabels);
    return () => window.removeEventListener('labelsUpdated', reloadLabels);
  }, [queryClient]);

  // Load table preferences from settings (sorting, columns, footer) + custom field defs,
  // once per table: a kept tab shown again runs its effects again, and must not put the
  // saved values back over what was changed since. Read through the query cache, so
  // the tables on one page share one request.
  const preferencesFor = useRef(null);
  useEffect(() => {
    const preferencesKey = JSON.stringify([tableId, projectId, !!externalVisibleColumns, showFilters, readOnly, defaultSortColumn]);
    if (preferencesFor.current === preferencesKey) return;
    preferencesFor.current = preferencesKey;
    if (readOnly) {  // the defaults; a visitor has no saved settings
      setSortLoaded(true);
      return;
    }
    const loadTablePreferences = async () => {
      try {
        const [settings, defsData] = await Promise.all([
          queryClient.ensureQueryData({ queryKey: settingsKeys.current(), queryFn: () => settingsApi.get().then(r => r.data) }),
          queryClient.ensureQueryData({ queryKey: customFieldKeys.list(projectId), queryFn: () => customFieldsApi.getAll(projectId).then(r => r.data) }),
        ]);
        const response = { data: settings };

        const defs = defsData || [];
        setCustomFieldDefs(defs);

        // Load sorting preferences
        const tableSorting = response.data?.table_sorting?.[tableId];
        if (tableSorting) {
          setSortColumn(tableSorting.column || defaultSortColumn);
          setSortDirection(tableSorting.direction || 'asc');
        }

        // Load column config (order applies always; visibility only when not external)
        const tableConfig = response.data?.table_config?.[tableId];

        // Load grouping + filters (persisted per-user, per-table). tableId
        // already encodes the view id + section, so these stick per view/section.
        if (typeof tableConfig?.groupBy === 'string') {
          setGroupBy(tableConfig.groupBy);
        }
        // Only hydrate filters when this table owns them. When the page owns
        // filtering (showFilters=false) the shared bar loads the same key.
        if (showFilters && Array.isArray(tableConfig?.filterGroups)) {
          setFilterGroups(tableConfig.filterGroups);
        }
        if (typeof tableConfig?.showCompleted === 'boolean') {
          setShowCompleted(tableConfig.showCompleted);
        }

        if (tableConfig?.columns) {
          if (!externalVisibleColumns) {
            const cols = {};
            // The built-in keys and defaults (the labels are not needed, so a language change does not reload)
            COLUMN_DEFINITIONS_BASE.forEach(col => {
              cols[col.key] = tableConfig.columns[col.key]?.visible ?? (col.defaultVisible ?? true);
            });
            // Custom field columns: read saved visibility or default to true
            defs.forEach(def => {
              const key = `cf_${def.id}`;
              cols[key] = tableConfig.columns[key]?.visible ?? true;
            });
            setInternalVisibleColumns(cols);
          }

          // Load column order (always, regardless of external visibility control)
          const orderedBuiltin = COLUMN_DEFINITIONS_BASE
            .map(col => ({
              key: col.key,
              order: tableConfig.columns[col.key]?.order ?? COLUMN_DEFINITIONS_BASE.findIndex(c => c.key === col.key),
            }))
            .sort((a, b) => a.order - b.order)
            .map(item => item.key);

          // Custom field columns come after built-in ones, sorted by their saved order
          const customKeys = defs.map(def => {
            const key = `cf_${def.id}`;
            return {
              key,
              order: tableConfig.columns[key]?.order ?? (COLUMN_DEFINITIONS_BASE.length + defs.indexOf(def)),
            };
          }).sort((a, b) => a.order - b.order).map(item => item.key);

          setColumnOrder([...orderedBuiltin, ...customKeys]);
        } else if (defs.length > 0) {
          // No saved settings yet - append custom fields at the end
          setColumnOrder(prev => {
            const existingSet = new Set(prev);
            const newKeys = defs.map(d => `cf_${d.id}`).filter(k => !existingSet.has(k));
            return newKeys.length > 0 ? [...prev, ...newKeys] : prev;
          });
          if (!externalVisibleColumns) {
            setInternalVisibleColumns(prev => {
              const next = { ...prev };
              defs.forEach(def => { next[`cf_${def.id}`] = true; });
              return next;
            });
          }
        }

        if (!externalVisibleColumns && tableConfig?.footer) {
          setInternalFooterConfig(prev => ({
            ...prev,
            ...Object.fromEntries(
              Object.entries(tableConfig.footer).map(([key, val]) => [key, { function: val.function || 'none' }])
            )
          }));
        }

        setSortLoaded(true);
      } catch (err) {
        console.error('Failed to load table preferences:', err);
        setSortLoaded(true);
      }
    };
    loadTablePreferences();
  }, [tableId, projectId, externalVisibleColumns, showFilters, readOnly, defaultSortColumn, queryClient]);

  // Persist grouping + filters per-user-per-table, mirroring the sort/column
  // persistence above. Gated on sortLoaded (so the values hydrated by the load
  // above aren't immediately written back) via a one-shot ref, and debounced so
  // typing inside a filter condition doesn't fire one request per keystroke.
  const viewPrefsHydrated = useRef(false);
  const pendingViewPrefsRef = useRef(null);

  const saveViewPrefs = useCallback((id, prefs) => {
    const payload = {
      [`table_config.${id}.groupBy`]: prefs.groupBy,
      [`table_config.${id}.showCompleted`]: prefs.showCompleted,
    };
    // When the page owns filtering (showFilters=false, e.g. the shared filter bar
    // on the tasks/home/project pages), don't persist filterGroups here - the
    // page-level filter writes the same key, and this stale copy would clobber it.
    if (showFilters) payload[`table_config.${id}.filterGroups`] = prefs.filterGroups;
    saveSettings(payload).catch(err => console.error('Failed to save view preferences:', err));
  }, [showFilters, saveSettings]);

  useEffect(() => {
    if (!sortLoaded) return;
    if (!viewPrefsHydrated.current) {
      viewPrefsHydrated.current = true;
      return;
    }
    pendingViewPrefsRef.current = { id: tableId, prefs: { groupBy, filterGroups, showCompleted } };
    const handle = setTimeout(() => {
      pendingViewPrefsRef.current = null;
      saveViewPrefs(tableId, { groupBy, filterGroups, showCompleted });
    }, 600);
    return () => clearTimeout(handle);
  }, [groupBy, filterGroups, showCompleted, sortLoaded, tableId, saveViewPrefs]);

  // Flush a pending debounced save when the table unmounts (or switches to a
  // different tableId) so a change made right before navigating away isn't
  // lost. Mirrors the flush-on-unmount in useViewTabs.
  useEffect(() => () => {
    const pending = pendingViewPrefsRef.current;
    if (pending) {
      pendingViewPrefsRef.current = null;
      saveViewPrefs(pending.id, pending.prefs);
    }
  }, [tableId, saveViewPrefs]);

  // Called after a new custom field is created via AddFieldModal
  const handleFieldCreated = useCallback((newDef) => {
    setCustomFieldDefs(prev => {
      if (prev.find(d => d.id === newDef.id)) return prev;
      return [...prev, newDef];
    });
    const key = `cf_${newDef.id}`;
    setInternalVisibleColumns(prev => (key in prev ? prev : { ...prev, [key]: true }));
    setColumnOrder(prev => prev.includes(key) ? prev : [...prev, key]);
    window.dispatchEvent(new CustomEvent('customFieldsUpdated'));
  }, []);

  // Called after a custom field is updated via AddFieldModal edit mode
  const handleFieldUpdated = useCallback((updatedDef) => {
    setCustomFieldDefs(prev => prev.map(d => d.id === updatedDef.id ? updatedDef : d));
    setEditingFieldDef(null);
    window.dispatchEvent(new CustomEvent('customFieldsUpdated'));
  }, []);

  // Delete a custom field definition
  const handleDeleteField = useCallback(async (fieldId) => {
    try {
      await customFieldsApi.delete(fieldId);
      const key = `cf_${fieldId}`;
      setCustomFieldDefs(prev => prev.filter(d => d.id !== fieldId));
      setInternalVisibleColumns(prev => { const next = { ...prev }; delete next[key]; return next; });
      setColumnOrder(prev => prev.filter(k => k !== key));
      setConfirmDeleteField(null);
      toast.success(t('tasks.field_deleted'));
      window.dispatchEvent(new CustomEvent('customFieldsUpdated'));
    } catch {
      toast.error(t('tasks.field_delete_failed'));
    }
  }, [t]);

  // Close field menu when clicking outside
  useEffect(() => {
    if (!fieldMenuOpen) return;
    const handler = () => { setFieldMenuOpen(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fieldMenuOpen]);

  // Save sorting preferences to settings
  const saveSortPreferences = async (column, direction) => {
    try {
      await saveSettings({
        [`table_sorting.${tableId}`]: { column, direction }
      });
    } catch (err) {
      console.error('Failed to save sort preferences:', err);
    }
  };

  // Save column order to settings (uses .order sub-field to avoid overwriting .visible)
  const saveColumnOrder = useCallback(async (newOrder) => {
    try {
      const updateData = {};
      newOrder.forEach((key, idx) => {
        updateData[`table_config.${tableId}.columns.${key}.order`] = idx;
      });
      await saveSettings(updateData);
    } catch (err) {
      console.error('Failed to save column order:', err);
    }
  }, [tableId, saveSettings]);

  // Toggle column visibility (skip if using external control)
  // Uses .visible sub-field to avoid overwriting .order
  const handleToggleColumn = useCallback(async (columnKey) => {
    if (externalVisibleColumns) return; // External control handles this

    const newValue = !visibleColumns[columnKey];
    setInternalVisibleColumns(prev => ({ ...prev, [columnKey]: newValue }));

    try {
      await saveSettings({
        [`table_config.${tableId}.columns.${columnKey}.visible`]: newValue
      });
    } catch (err) {
      console.error('Failed to save column visibility:', err);
    }
  }, [visibleColumns, tableId, externalVisibleColumns, saveSettings]);

  // Drag-and-drop column reorder handlers
  const handleColDragStart = useCallback((key) => {
    dragColRef.current = key;
  }, []);

  const handleColDragOver = useCallback((e, key) => {
    e.preventDefault();
    if (dragColRef.current && dragColRef.current !== key) {
      setDragOverCol(key);
    }
  }, []);

  const handleColDrop = useCallback((e, targetKey) => {
    e.preventDefault();
    const fromKey = dragColRef.current;
    if (!fromKey || fromKey === targetKey) {
      setDragOverCol(null);
      dragColRef.current = null;
      return;
    }
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const fromIdx = newOrder.indexOf(fromKey);
      const toIdx = newOrder.indexOf(targetKey);
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, fromKey);
      saveColumnOrder(newOrder);
      return newOrder;
    });
    setDragOverCol(null);
    dragColRef.current = null;
  }, [saveColumnOrder]);

  const handleColDragEnd = useCallback(() => {
    setDragOverCol(null);
    dragColRef.current = null;
  }, []);

  // Open inline cell editor for a specific cell
  const openCellEditor = useCallback((e, task, columnKey) => {
    e.stopPropagation();
    if (readOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // Estimate content height and width per column type
    const contentHeight = columnKey === 'due_date' ? 460 : columnKey === 'project' ? 380 : columnKey === 'duration' ? 220 : 200;
    const dropdownWidth = columnKey === 'due_date' || columnKey === 'project' ? 288 : columnKey === 'duration' ? 240 : 200;
    const spaceBelow = window.innerHeight - rect.bottom - 6;
    const spaceAbove = rect.top - 6;
    // Prefer showing below; show above only when there's more room above than below
    const showAbove = spaceBelow < contentHeight && spaceAbove > spaceBelow;
    const left = Math.min(rect.left, window.innerWidth - dropdownWidth - 8);
    const dropdownStyle = showAbove
      ? { bottom: window.innerHeight - rect.top + 6, left, minWidth: 160, maxHeight: spaceAbove, overflowY: 'auto' }
      : { top: rect.bottom + 6, left, minWidth: 160, maxHeight: spaceBelow, overflowY: 'auto' };
    setEditingCell({ taskId: task.id, columnKey, rect, task, dropdownStyle });
  }, [readOnly]);

  // Apply the inline cell change via API
  const handleCellUpdate = useCallback(async (taskId, field, value) => {
    // Optimistic update: apply immediately in local state
    setPendingUpdates(withPending(taskId, field, value));
    onOptimisticUpdate?.(taskId, { [field]: value });
    setEditingCell(null);
    try {
      await tasksApi.update(taskId, { [field]: value });
      // Clear pending override for this field once confirmed
      setPendingUpdates(withoutPending(taskId, field));
    } catch (err) {
      console.error('Error updating task cell:', err);
      // Revert optimistic override and re-fetch on error
      setPendingUpdates(withoutPending(taskId, field));
      onUpdate?.();
    }
  }, [onUpdate, onOptimisticUpdate]);

  // Close inline editor when clicking outside or pressing Escape
  useEffect(() => {
    if (!editingCell) return;
    const onMouseDown = (e) => {
      if (inlineDropdownRef.current && !inlineDropdownRef.current.contains(e.target)) {
        setEditingCell(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setEditingCell(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [editingCell]);

  // Update a date/time field via API without closing the editor
  const handleDateFieldUpdate = useCallback(async (taskId, field, value) => {
    setPendingUpdates(withPending(taskId, field, value));
    onOptimisticUpdate?.(taskId, { [field]: value });
    setEditingCell(prev => prev ? { ...prev, task: { ...prev.task, [field]: value } } : null);
    try {
      await tasksApi.update(taskId, { [field]: value });
      setPendingUpdates(withoutPending(taskId, field));
    } catch (err) {
      console.error('Error updating task date field:', err);
      setPendingUpdates(withoutPending(taskId, field));
      onUpdate?.();
    }
  }, [onUpdate, onOptimisticUpdate]);

  // Save inline title edit
  const handleTitleSave = useCallback(async (taskId, newTitle) => {
    const trimmed = newTitle.trim();
    setEditingTitleId(null);
    if (!trimmed) return;
    onOptimisticUpdate?.(taskId, { title: trimmed });
    try {
      await tasksApi.update(taskId, { title: trimmed });
    } catch (err) {
      console.error('Error updating task title:', err);
      onUpdate?.();
    }
  }, [onUpdate, onOptimisticUpdate]);

  // Update footer aggregation config
  const handleFooterConfigChange = useCallback(async (columnKey, fn) => {
    // Use external handler if provided
    if (onExternalFooterConfigChange) {
      onExternalFooterConfigChange(columnKey, fn);
      return;
    }

    setInternalFooterConfig(prev => ({ ...prev, [columnKey]: { function: fn } }));

    try {
      await saveSettings({
        [`table_config.${tableId}.footer.${columnKey}`]: { function: fn }
      });
    } catch (err) {
      console.error('Failed to save footer config:', err);
    }
  }, [tableId, onExternalFooterConfigChange, saveSettings]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    let newDirection = 'asc';
    if (sortColumn === column) {
      // Ascending, descending, then back to the order tasks were dragged into.
      if (sortDirection === 'desc' && defaultSortColumn === 'manual') {
        setSortColumn('manual');
        setSortDirection('asc');
        saveSortPreferences('manual', 'asc');
        return;
      }
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortColumn(column);
    setSortDirection(newDirection);
    saveSortPreferences(column, newDirection);
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const getProjectById = (projectId) => projects.find(p => p.id === projectId);

  // A subtask whose parent is not in this list shows the parent's title under its own.
  // The parent may be outside the list (another day, filtered out), so look in every task.
  const { data: allTasks } = useTasksQuery({}, { enabled: !readOnly });
  const taskTitles = useMemo(() => {
    const titles = new Map();
    (allTasks || []).forEach((task) => titles.set(task.id, task.title));
    tasks.forEach((task) => titles.set(task.id, task.title));
    return titles;
  }, [allTasks, tasks]);

  // Check if a task is in a "completed" state (handles both old status and new status_id)
  const isTaskCompleted = useCallback((task) => {
    if (task.status_id && customStatuses.length > 0) {
      const cs = customStatuses.find(s => s.id === task.status_id);
      if (cs) return cs.category === 'completed';
    }
    return task.status === 'completed';
  }, [customStatuses]);

  const handleToggleComplete = useCallback(async (e, task) => {
    e.stopPropagation();
    if (readOnly) return;
    const wasCompleted = isTaskCompleted(task);
    const actionLabel = wasCompleted
      ? t('tasks.marked_incomplete_undo', { name: task.title })
      : t('tasks.marked_complete_undo', { name: task.title });
    try {
      if (task.status_id && customStatuses.length > 0) {
        const completed = wasCompleted;
        const targetCategory = completed ? 'not_started' : 'completed';
        const targetStatus = customStatuses.find(s => s.category === targetCategory);
        if (targetStatus) {
          const prevStatusId = task.status_id;
          onOptimisticUpdate?.(task.id, { status_id: targetStatus.id });
          await tasksApi.update(task.id, { status_id: targetStatus.id });
          pushUndo({
            label: actionLabel,
            undo: async () => {
              onOptimisticUpdate?.(task.id, { status_id: prevStatusId });
              await tasksApi.update(task.id, { status_id: prevStatusId });
            },
          });
        } else {
          const prevStatus = task.status;
          const newStatus = completed ? 'todo' : 'completed';
          onOptimisticUpdate?.(task.id, { status: newStatus });
          await tasksApi.update(task.id, { status: newStatus });
          pushUndo({
            label: actionLabel,
            undo: async () => {
              onOptimisticUpdate?.(task.id, { status: prevStatus });
              await tasksApi.update(task.id, { status: prevStatus });
            },
          });
        }
      } else {
        const prevStatus = task.status;
        const newStatus = prevStatus === 'completed' ? 'todo' : 'completed';
        onOptimisticUpdate?.(task.id, { status: newStatus });
        await tasksApi.update(task.id, { status: newStatus });
        pushUndo({
          label: actionLabel,
          undo: async () => {
            onOptimisticUpdate?.(task.id, { status: prevStatus });
            await tasksApi.update(task.id, { status: prevStatus });
          },
        });
      }
    } catch (err) {
      console.error('Error updating task:', err);
      onUpdate?.(); // restore on error
    }
  }, [isTaskCompleted, t, customStatuses, onOptimisticUpdate, pushUndo, onUpdate, readOnly]);

  // Render a single task cell by column key
  // nest: where the row sits among subtasks - { depth, childCount, parentTitle } (lib/subtasks.js)
  const renderTaskCell = useCallback((key, task, priority, status, dueInfo, taskProject, nest = null) => {
    const cellClass = CELL_WRAPPER_CLASS[key];
    switch (key) {
      case 'title': {
        const completed = isTaskCompleted(task);
        const isEditingTitle = editingTitleId === task.id;
        const progress = subtaskProgress(task);
        const subtasksOpen = nest?.childCount > 0 && !collapsedParents.has(task.id);
        return (
          <div key={key} className={cellClass} style={nest?.depth ? { paddingLeft: nest.depth * 24 } : undefined}>
            <button onClick={(e) => handleToggleComplete(e, task)} disabled={readOnly} className="flex-shrink-0">
              {completed ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Circle className={`w-5 h-5 ${circleTone(task.priority)}`} />
              )}
            </button>
            <div className="min-w-0 flex-1">
              {isEditingTitle ? (
                <input
                  ref={titleInputRef}
                  autoFocus
                  value={editingTitleValue}
                  onChange={e => setEditingTitleValue(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onBlur={() => handleTitleSave(task.id, editingTitleValue)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); handleTitleSave(task.id, editingTitleValue); }
                    if (e.key === 'Escape') { e.stopPropagation(); setEditingTitleId(null); }
                  }}
                  className="w-full bg-transparent text-foreground border-b border-amber-500/60 outline-none text-sm py-0.5"
                />
              ) : (
                <div className="flex items-center gap-1 min-w-0 group/title">
                  <span className={`truncate ${completed ? 'text-muted-foreground line-through' : 'text-foreground'} ${hasUnread(task.id) ? 'font-semibold' : 'font-normal'}`}>
                    {task.title}
                  </span>
                  <UnreadBadge taskId={task.id} className="flex-shrink-0" />
                  <SpotlightMark taskId={task.id} mark={spotlight.items[task.id]} sections={spotlight.sections} addable={!readOnly} />
                  {task.is_locked && <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                  {task.attachments?.length > 0 && (
                    <span className="flex items-center gap-0.5 text-xs text-muted-foreground/40 flex-shrink-0">
                      <Paperclip className="w-3 h-3" />
                      {task.attachments.length}
                    </span>
                  )}
                  {(progress || nest?.childCount > 0) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); if (nest?.childCount > 0) toggleSubtasks(task.id); }}
                      disabled={!(nest?.childCount > 0)}
                      className="flex items-center gap-0.5 text-xs text-muted-foreground/70 hover:text-foreground disabled:hover:text-muted-foreground/70 flex-shrink-0 tabular-nums"
                      title={t('tasks.subtasks')}
                      aria-label={t('tasks.subtasks')}
                      aria-expanded={nest?.childCount > 0 ? subtasksOpen : undefined}
                    >
                      {subtasksOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      {progress ? `${progress.done}/${progress.total}` : nest.childCount}
                    </button>
                  )}
                  {!task.is_locked && !readOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTitleId(task.id);
                        setEditingTitleValue(task.title);
                      }}
                      className="flex-shrink-0 opacity-0 group-hover/title:opacity-100 focus:opacity-100 p-0.5 text-muted-foreground/70 hover:text-foreground transition-opacity"
                      title={t('tasks.rename', { defaultValue: 'Rename' })}
                      aria-label={t('tasks.rename', { defaultValue: 'Rename' })}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); openTaskDetail(task.id, { teamId }); }}
                    className="flex-shrink-0 opacity-0 group-hover/title:opacity-100 p-0.5 text-muted-foreground/70 hover:text-foreground transition-opacity"
                    title={t('ui_views.table.open_task_detail')}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              {!isEditingTitle && ((showProjectColumn && groupBy !== 'project' && taskProject) || nest?.parentTitle) && (
                <div className="mt-0.5 flex items-center gap-1 min-w-0 text-xs text-muted-foreground">
                  {showProjectColumn && groupBy !== 'project' && taskProject && <ProjectChip project={taskProject} />}
                  {showProjectColumn && groupBy !== 'project' && taskProject && nest?.parentTitle && <span className="text-muted-foreground/50">/</span>}
                  {nest?.parentTitle && <span className="truncate">{nest.parentTitle}</span>}
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'status': {
        // Honey graphite: a colored dot and the name, no colored box.
        // Prefer custom status (status_id) over old status field
        let statusLabel = legacyStatusName(task.status);
        let dotStyle;
        let dotClass = status.color;
        if (task.status_id && customStatuses.length > 0) {
          const cs = customStatuses.find(s => s.id === task.status_id);
          if (cs) {
            statusLabel = statusName(cs, t);
            dotStyle = { backgroundColor: cs.color };
            dotClass = '';
          }
        }
        return (
          <div key={key} className={`${cellClass} group/cell cursor-pointer`} onClick={(e) => openCellEditor(e, task, 'status')}>
            <button className={`${CELL_CHIP} text-foreground/80`}>
              <span className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${dotClass}`} style={dotStyle} />
              <span className="truncate">{statusLabel}</span>
            </button>
          </div>
        );
      }
      case 'priority': {
        // Bars, like a signal: urgent is the only one in color.
        const level = { p1: 3, p2: 3, p3: 2, p4: 1 }[task.priority] || 1;
        const barOn = task.priority === 'p1' ? 'bg-red-500' : 'bg-foreground/80';
        return (
          <div key={key} className={`${cellClass} group/cell cursor-pointer`} onClick={(e) => openCellEditor(e, task, 'priority')}>
            <span className={`${CELL_CHIP} gap-2 text-muted-foreground pointer-events-none`}>
              <span className="inline-flex items-end gap-[2px] h-3" aria-hidden="true">
                {[5, 8, 11].map((h, i) => (
                  <span key={h} className={`w-[3px] rounded-[1px] ${i < level ? barOn : 'bg-border'}`} style={{ height: h }} />
                ))}
              </span>
              {t(priority.altLabelKey)}
            </span>
          </div>
        );
      }
      case 'due_date':
        // The same date picker as the task's detail (components/task/pickers/DatePicker).
        return (
          <DatePicker
            key={key}
            value={task.due_date}
            onChange={(date) => handleDateFieldUpdate(task.id, 'due_date', date ? toLocalDateString(date) : null)}
            timeValue={task.due_time}
            onTimeChange={(value) => handleDateFieldUpdate(task.id, 'due_time', value || null)}
            disabled={readOnly}
          >
            <div className={`${cellClass} group/cell cursor-pointer`} onClick={(e) => e.stopPropagation()} data-testid="due-cell">
              <span className={`${CELL_CHIP} tabular-nums pointer-events-none ${dueInfo ? dueInfo.className : 'text-muted-foreground/60'}`}>
                {dueInfo ? dueInfo.text : t('tasks.no_date')}
              </span>
            </div>
          </DatePicker>
        );
      case 'duration':
        return (
          <div key={key} className={`${cellClass} group/cell cursor-pointer`} onClick={(e) => openCellEditor(e, task, 'duration')}>
            <span className={`${CELL_CHIP} tabular-nums text-muted-foreground pointer-events-none`}>
              {task.duration ? t('ui_task.duration.minutes', { m: task.duration }) : '-'}
            </span>
          </div>
        );
      case 'project':
        return (
          <div key={key} className={`${cellClass} group/cell`}>
            <button
              onClick={(e) => openCellEditor(e, task, 'project')}
              className={`${CELL_CHIP} text-left`}
            >
              {taskProject ? (
                <ProjectChip project={taskProject} textClass="text-xs text-foreground/80" />
              ) : (
                <span className="text-xs text-muted-foreground/70 hover:text-muted-foreground">-</span>
              )}
            </button>
          </div>
        );
      case 'labels': {
        const taskLabels = (task.label_ids || []).map(id => labels.find(l => l.id === id)).filter(Boolean);
        return (
          <div key={key} className={cellClass}>
            {taskLabels.length > 0 ? (
              taskLabels.slice(0, 2).map(label => (
                <span
                  key={label.id}
                  className="px-1.5 py-0.5 rounded-full text-xs truncate max-w-full"
                  style={{ backgroundColor: `${label.color}30`, color: label.color }}
                  title={label.name}
                >
                  {label.name}
                </span>
              ))
            ) : (
              <span className="text-xs text-muted-foreground/70">-</span>
            )}
            {taskLabels.length > 2 && (
              <span className="text-xs text-muted-foreground shrink-0">+{taskLabels.length - 2}</span>
            )}
          </div>
        );
      }
      case 'scheduled':
        return (
          <div key={key} className={`${cellClass} group/cell`}>
            {task.scheduled_start ? (
              <span className="text-xs tabular-nums text-foreground/80">{formatDate(new Date(task.scheduled_start))}</span>
            ) : (
              <a
                href="/app/planner"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = '/app/planner'; }}
                className={`${CELL_CHIP} text-muted-foreground/70 cursor-pointer`}
              >
                {t('tasks.schedule')}
              </a>
            )}
          </div>
        );
      case 'task_type': {
        const tt = taskTypes.find(t => t.id === task.task_type_id);
        if (!tt) return <div key={key} className={cellClass}><span className="text-xs text-muted-foreground/70">-</span></div>;
        const IconComp = taskTypeIcons[tt.icon] || CheckSquare;
        return (
          <div key={key} className={cellClass}>
            <span
              className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: `${tt.color}25`, color: tt.color }}
            >
              <IconComp className="w-3 h-3 shrink-0" />
              {tt.name}
            </span>
          </div>
        );
      }
      case 'assignee': {
        const assignees = (task.assigned_to || []).map(uid => usersMap[uid]).filter(Boolean);
        const bot = task.assigned_bot_id ? botsById[task.assigned_bot_id] : null;
        return (
          <div
            key={key}
            className={`${cellClass} group/cell cursor-pointer`}
            onClick={(e) => openCellEditor(e, task, 'assignee')}
          >
            <div className={`${CELL_CHIP} py-0.5 gap-0 text-muted-foreground`}>
            {bot && (
              <span title={botLabel(bot)} className="shrink-0 -ml-1 first:ml-0">
                <BotAvatar bot={bot} size="sm" />
              </span>
            )}
            {assignees.length === 0 ? (bot ? null : (
              <span className="text-xs text-muted-foreground/70">-</span>
            )) : (
              <>
                {assignees.slice(0, 3).map(m => (
                  m.avatar_url ? (
                    <img
                      key={m.user_id}
                      src={m.avatar_url}
                      alt={m.full_name}
                      title={m.full_name}
                      className="w-6 h-6 rounded-full object-cover shrink-0 -ml-1 first:ml-0 border border-background"
                    />
                  ) : (
                    <div
                      key={m.user_id}
                      title={m.full_name}
                      className="w-6 h-6 rounded-full bg-indigo-400 dark:bg-indigo-600 flex items-center justify-center text-xs font-medium text-white shrink-0 -ml-1 first:ml-0 border border-background"
                    >
                      {m.full_name?.charAt(0)?.toUpperCase() || <User className="w-3 h-3" />}
                    </div>
                  )
                ))}
                {assignees.length > 3 && (
                  <span className="text-xs text-muted-foreground ml-1">+{assignees.length - 3}</span>
                )}
              </>
            )}
            </div>
          </div>
        );
      }
      case 'owner': {
        const owner = task.user_id ? usersMap[task.user_id] : null;
        if (!owner) return <div key={key} className={cellClass}><span className="text-xs text-muted-foreground/70">-</span></div>;
        return (
          <div key={key} className={cellClass}>
            {owner.avatar_url ? (
              <img src={owner.avatar_url} alt={owner.full_name} title={owner.full_name} className="w-6 h-6 rounded-full object-cover shrink-0 border border-background" />
            ) : (
              <div
                title={owner.full_name}
                className="w-6 h-6 rounded-full bg-violet-400 dark:bg-violet-600 flex items-center justify-center text-xs font-medium text-white shrink-0 border border-background"
              >
                {owner.full_name?.charAt(0)?.toUpperCase() || <User className="w-3 h-3" />}
              </div>
            )}
            <span className="text-xs text-muted-foreground truncate">{owner.full_name || owner.email || task.user_id}</span>
          </div>
        );
      }
      case 'team': {
        const effectiveTeamId = task.team_id
          || projects.find(p => p.id === task.project_id)?.team_id;
        const team = effectiveTeamId ? teamsMap[effectiveTeamId] : null;
        return (
          <div key={key} className={`${cellClass} group/cell`}>
            <button
              onClick={(e) => openCellEditor(e, task, 'team')}
              className={`${CELL_CHIP} text-left truncate`}
            >
              {team ? (
                <span className="text-foreground/80 truncate">{team.name}</span>
              ) : (
                <span className="text-muted-foreground/70">-</span>
              )}
            </button>
          </div>
        );
      }
      case 'created_at':
        return (
          <div key={key} className={cellClass}>
            {task.created_at ? (
              <span className="text-xs text-muted-foreground">{formatDate(new Date(task.created_at))}</span>
            ) : (
              <span className="text-xs text-muted-foreground/70">-</span>
            )}
          </div>
        );
      default: {
        // Custom field columns (keys start with "cf_")
        if (key.startsWith('cf_')) {
          const def = customFieldDefs.find(d => `cf_${d.id}` === key);
          if (!def) return null;
          return (
            <div key={key} className="hidden sm:flex col-span-2 items-center min-w-0">
              <CustomFieldCell
                def={def}
                value={task.custom_fields?.[def.id]}
                taskId={task.id}
                onUpdate={onUpdate}
              />
            </div>
          );
        }
        return null;
      }
    }
  }, [showProjectColumn, groupBy, handleToggleComplete, isTaskCompleted, customStatuses, labels, formatDate, openCellEditor, taskTypes, usersMap, teamsMap, projects, customFieldDefs, onUpdate, editingTitleId, editingTitleValue, handleTitleSave, t,
    botLabel, botsById, hasUnread, legacyStatusName, openTaskDetail, teamId, readOnly, collapsedParents, toggleSubtasks,
    handleDateFieldUpdate, toLocalDateString, spotlight]);

  // Completed count for the toggle button
  const completedCount = useMemo(() => tasks.filter(t => {
    if (t.status_id && customStatuses.length > 0) {
      const cs = customStatuses.find(s => s.id === t.status_id);
      if (cs) return cs.category === 'completed';
    }
    return t.status === 'completed';
  }).length, [tasks, customStatuses]);

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    // Apply any pending optimistic overrides
    const pending = pendingUpdates;
    const effectiveTasks = Object.keys(pending).length > 0
      ? tasks.map(t => pending[t.id] ? { ...t, ...pending[t.id] } : t)
      : tasks;

    // First filter
    const filtered = effectiveTasks.filter(task => {
      // Hide completed tasks unless showCompleted is true (only when toggle is enabled)
      if (showCompletedToggle && !showCompleted) {
        const matchedStatus = task.status_id && customStatuses.length > 0
          ? customStatuses.find(s => s.id === task.status_id)
          : null;
        const done = matchedStatus
          ? matchedStatus.category === 'completed'
          : task.status === 'completed';
        if (done) return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!task.title.toLowerCase().includes(query) &&
            !(extractPlainText(task.description) || '').toLowerCase().includes(query)) {
          return false;
        }
      }

      // (A OR B) AND (C OR D) - each group is OR, groups are ANDed.
      // Only "complete" conditions count, so a freshly-added blank condition
      // (e.g. from opening the filter panel) doesn't filter anything out.
      if (filterGroups.length > 0) {
        const pass = filterGroups.every(group => {
          const active = group.conditions.filter(conditionIsActive);
          return active.length === 0 || active.some(cond => evaluateCondition(task, cond));
        });
        if (!pass) return false;
      }

      return true;
    });

    // Then sort
    const priorityOrder = { p1: 1, p2: 2, p3: 3, p4: 4 };
    const statusOrder = { todo: 1, in_progress: 2, completed: 3 };
    const userName = (uid) => (usersMap[uid]?.full_name || usersMap[uid]?.email || '').toLowerCase();
    const projName = (t) => (projects.find(p => p.id === t.project_id)?.name || '').toLowerCase();
    const num = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
    const compareCustomField = (a, b) => {
      const def = customFieldDefs.find(d => `cf_${d.id}` === sortColumn);
      if (!def) return 0;
      const va = a.custom_fields?.[def.id];
      const vb = b.custom_fields?.[def.id];
      switch (def.field_type) {
        case 'checkbox': return (va ? 1 : 0) - (vb ? 1 : 0);
        case 'number':
        case 'rating':  return num(va) - num(vb);
        case 'date':    return (va ? new Date(va).getTime() : Infinity) - (vb ? new Date(vb).getTime() : Infinity);
        case 'select':  return ((def.options?.find(o => o.id === va)?.label) || '')
                                 .localeCompare((def.options?.find(o => o.id === vb)?.label) || '');
        case 'multi_select': {
          const lbl = (v) => (Array.isArray(v) ? v : []).map(id => def.options?.find(o => o.id === id)?.label || '').join(', ');
          return lbl(va).localeCompare(lbl(vb));
        }
        default: return String(va ?? '').localeCompare(String(vb ?? ''));
      }
    };

    return [...filtered].sort((a, b) => {
      let comparison;
      switch (sortColumn) {
        case 'manual':
          comparison = ((a.order ?? Infinity) - (b.order ?? Infinity))
            || (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
          break;
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'status':
          comparison = (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
          break;
        case 'priority':
          comparison = (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
          break;
        case 'due_date': {
          const dateA = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          const dateB = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          comparison = dateA - dateB;
          break;
        }
        case 'duration':
          comparison = (a.duration || 0) - (b.duration || 0);
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        case 'scheduled': {
          const sa = a.scheduled_start ? new Date(a.scheduled_start).getTime() : Infinity;
          const sb = b.scheduled_start ? new Date(b.scheduled_start).getTime() : Infinity;
          comparison = sa - sb;
          break;
        }
        case 'owner':
          comparison = userName(a.user_id).localeCompare(userName(b.user_id));
          break;
        case 'assignee':
          comparison = userName(a.assigned_to?.[0]).localeCompare(userName(b.assigned_to?.[0]));
          break;
        case 'project':
          comparison = projName(a).localeCompare(projName(b));
          break;
        case 'team': {
          const at = a.team_id || projects.find(p => p.id === a.project_id)?.team_id;
          const bt = b.team_id || projects.find(p => p.id === b.project_id)?.team_id;
          comparison = (teamsMap[at]?.name || '').toLowerCase().localeCompare((teamsMap[bt]?.name || '').toLowerCase());
          break;
        }
        case 'task_type': {
          const an = (taskTypes.find(tt => tt.id === a.task_type_id)?.name || '').toLowerCase();
          const bn = (taskTypes.find(tt => tt.id === b.task_type_id)?.name || '').toLowerCase();
          comparison = an.localeCompare(bn);
          break;
        }
        case 'labels':
          comparison = (a.label_ids?.length || 0) - (b.label_ids?.length || 0);
          break;
        default:
          // Custom field columns (cf_<id>) — sort by the field's value/type.
          comparison = sortColumn.startsWith('cf_') ? compareCustomField(a, b) : 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [tasks, pendingUpdates, searchQuery, filterGroups, evaluateCondition, sortColumn, sortDirection, showCompleted, showCompletedToggle, customStatuses, usersMap, projects, teamsMap, taskTypes, customFieldDefs]);

  // Group tasks
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All Tasks': { label: t('tasks.filters.all_tasks'), tasks: filteredTasks, project: null } };
    }

    const groups = {};

    filteredTasks.forEach(task => {
      let groupKey;
      let groupLabel;

      switch (groupBy) {
        case 'project': {
          // One "No Project" group: a task of a trashed project has none here either.
          const projectId = shownProjectId(task, projects);
          groupKey = `project:${projectId || NO_PROJECT}`;
          groupLabel = projects.find(p => p.id === projectId)?.name || t('tasks.no_project');
          break;
        }
        case 'status': {
          // Resolve the task's status to a canonical (category, name) pair.
          let stCategory = null;
          let stName = null;
          if (task.status_id && customStatuses.length > 0) {
            const cs = customStatuses.find(s => s.id === task.status_id);
            if (cs) { stCategory = cs.category; stName = cs.name; }
          }
          if (!stName) {
            // status_id missing or belongs to another member's/team's status set
            // not loaded here → fall back to the legacy enum.
            stCategory = LEGACY_TO_CATEGORY[task.status] || 'not_started';
            stName = statusConfig[task.status]?.label || t('tasks.status');
          }
          // Group by canonical identity (category + name), NOT the raw status_id:
          // every member/team has a distinct id for the same "To Do", so keying
          // by id produced a separate group per member. Same category + name =
          // one group.
          groupKey = `status:${stCategory}:${String(stName).trim().toLowerCase()}`;
          groupLabel = statusName({ name: stName, category: stCategory }, t);
          break;
        }
        case 'priority':
          groupKey = `priority:${task.priority || 'p4'}`;
          groupLabel = t(priorityConfig[task.priority]?.altLabelKey || 'bulk_action_bar.priority_low');
          break;
        default:
          groupKey = 'all';
          groupLabel = t('tasks.filters.all_tasks');
      }

      if (!groups[groupKey]) {
        groups[groupKey] = { label: groupLabel, tasks: [], project: groupBy === 'project' ? projects.find(p => p.id === task.project_id) : null };
      }
      groups[groupKey].tasks.push(task);
    });

    return groups;
  }, [filteredTasks, groupBy, projects, customStatuses, t]);

  // The rows of each group: subtasks under their parent when it is in the same group,
  // on their own otherwise (lib/subtasks.js). displayTasks: every row in screen order.
  const { groupRows, displayTasks } = useMemo(() => {
    const byGroup = {};
    const order = [];
    Object.entries(groupedTasks).forEach(([groupKey, group]) => {
      const rows = nestTasks(group.tasks || group, collapsedParents);
      byGroup[groupKey] = rows;
      rows.forEach((row) => order.push(row.task));
    });
    return { groupRows: byGroup, displayTasks: order };
  }, [groupedTasks, collapsedParents]);

  // ---- Drag and drop in a project list (as in ClickUp, Asana, Todoist) ----
  // Onto the middle of a row: the task becomes its subtask. Between rows: the task goes
  // there, on the level of the row below the line, which may take it out of its parent.
  // Into another list: the same, in that list. In manual order the drop sets the order of
  // the siblings; sorted by a column, it only changes where the task belongs.
  const qc = useQueryClient();
  const rowDropEnabled = !readOnly && (!!projectId || !!onManualOrder) && groupBy === 'none';
  const [dropTarget, setDropTarget] = useState(null); // { taskId, zone: 'before' | 'after' | 'into' }
  useEffect(() => {
    const clear = () => setDropTarget(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => { window.removeEventListener('dragend', clear); window.removeEventListener('drop', clear); };
  }, []);

  const lookupTask = useCallback(
    (id) => tasks.find((x) => x.id === id) || allTasks?.find((x) => x.id === id),
    [tasks, allTasks]);
  // Whether taskId is ancestorId or one of its subtasks (a task never goes under itself).
  const isWithin = useCallback((taskId, ancestorId) => {
    const seen = new Set();
    for (let cur = lookupTask(taskId); cur && !seen.has(cur.id); cur = cur.parent_id ? lookupTask(cur.parent_id) : null) {
      if (cur.id === ancestorId) return true;
      seen.add(cur.id);
    }
    return false;
  }, [lookupTask]);

  const dropZone = (e, rows, index) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - rect.top) / rect.height;
    if (onManualOrder) return { taskId: rows[index].task.id, zone: y >= 0.5 ? 'after' : 'before' };  // no nesting
    if (y > 0.25 && y < 0.75) return { taskId: rows[index].task.id, zone: 'into' };
    // The line below a row is the line above the next one: the task takes that row's level.
    if (y >= 0.75 && rows[index + 1]) return { taskId: rows[index + 1].task.id, zone: 'before' };
    return { taskId: rows[index].task.id, zone: y >= 0.75 ? 'after' : 'before' };
  };

  const handleRowDragOver = (e, rows, index) => {
    if (!rowDropEnabled || !draggingTaskId) return;
    const target = dropZone(e, rows, index);
    if (isWithin(target.taskId, draggingTaskId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget((prev) => (prev?.taskId === target.taskId && prev.zone === target.zone ? prev : target));
  };

  const handleRowDrop = async (e, rows) => {
    const target = dropTarget;
    const draggedId = draggingTaskId || e.dataTransfer.getData('text/plain');
    setDropTarget(null);
    if (!rowDropEnabled || !target || !draggedId) return;
    e.preventDefault();
    e.stopPropagation();
    if (onManualOrder) {
      const ids = rows.filter((r) => r.depth === 0).map((r) => r.task.id).filter((id) => id !== draggedId);
      const at = ids.indexOf(target.taskId);
      if (at < 0) return;
      ids.splice(at + (target.zone === 'after' ? 1 : 0), 0, draggedId);
      onManualOrder(ids, draggedId);
      return;
    }
    const dragged = lookupTask(draggedId);
    const targetRow = rows.find((r) => r.task.id === target.taskId);
    if (!dragged || !targetRow || isWithin(target.taskId, draggedId)) return;

    const parentId = target.zone === 'into' ? targetRow.task.id : (targetRow.depth > 0 ? targetRow.task.parent_id : null);
    // Its new siblings in the order on screen, with the task in its new place.
    const siblings = (parentId
      ? filteredTasks.filter((x) => x.parent_id === parentId)
      : rows.filter((r) => r.depth === 0).map((r) => r.task)
    ).filter((x) => x.id !== draggedId);
    if (target.zone === 'into') siblings.push(dragged);
    else siblings.splice(siblings.findIndex((x) => x.id === target.taskId) + (target.zone === 'after' ? 1 : 0), 0, dragged);

    const place = { parent_id: parentId, section_id: prefillSectionId || null, project_id: projectId };
    const patch = Object.fromEntries(Object.entries(place).filter(([k, v]) => (dragged[k] || null) !== v));
    const order = sortColumn === 'manual' ? siblings.map((x) => x.id) : null;
    if (!Object.keys(patch).length && !order) return;

    // Every list shows it at once; the server's answer refreshes them after.
    const orderOf = new Map((order || []).map((id, i) => [id, i]));
    qc.getQueriesData({ queryKey: taskKeys.lists() }).forEach(([key, list]) => {
      if (!Array.isArray(list)) return;
      qc.setQueryData(key, list.map((x) => {
        let next = x.id === draggedId ? { ...x, ...patch } : x;
        if (orderOf.has(x.id)) next = { ...next, order: orderOf.get(x.id) };
        return next;
      }));
    });
    if (parentId) {
      setCollapsedParents((prev) => {
        if (!prev.has(parentId)) return prev;
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
    }
    try {
      if (Object.keys(patch).length) await tasksApi.update(draggedId, patch);
      if (order) await tasksApi.reorder(order);
    } catch (err) {
      console.error('Failed to move task:', err);
      toast.error(t('tasks.task_save_failed'));
      qc.invalidateQueries({ queryKey: taskKeys.all });
    }
  };

  const toggleGroup = (groupKey) => {
    setExpanded(prev => ({ ...prev, [groupKey]: prev[groupKey] === false ? true : false }));
  };

  const handleTaskClick = (task) => {
    if (readOnly) return;  // as in ListView: the detail needs a signed-in member
    openTaskDetail(task.id, { teamId });
  };

  // Bulk-select: handle individual checkbox toggle with optional shift-range selection
  const handleCheckboxChange = useCallback((taskId, flatIndex, checked) => {
    setSelectedIds(prev => {
      let next = new Set(prev);
      if (window._shiftHeld && lastClickedIndexRef.current !== null) {
        const start = Math.min(lastClickedIndexRef.current, flatIndex);
        const end   = Math.max(lastClickedIndexRef.current, flatIndex);
        for (let i = start; i <= end; i++) {
          if (displayTasks[i]) {
            checked ? next.add(displayTasks[i].id) : next.delete(displayTasks[i].id);
          }
        }
      } else {
        checked ? next.add(taskId) : next.delete(taskId);
      }
      lastClickedIndexRef.current = flatIndex;
      onSelectionChange?.(next, filteredTasks.filter(t => next.has(t.id)), { customStatuses, projects });
      return next;
    });
  }, [filteredTasks, displayTasks, onSelectionChange, customStatuses, projects]);

  // Toggle bulk-select mode. Exiting clears any active selection so the
  // floating BulkActionBar disappears and the table snaps back to its
  // un-shifted layout.
  const toggleBulkSelectMode = useCallback(() => {
    setBulkSelectMode(prev => {
      const next = !prev;
      if (!next) {
        setSelectedIds(new Set());
        onSelectionChange?.(new Set(), [], { customStatuses, projects });
      }
      return next;
    });
  }, [onSelectionChange, customStatuses, projects]);

  // Track shift key for range selection
  useEffect(() => {
    const dn = (e) => { if (e.key === 'Shift') window._shiftHeld = true; };
    const up = (e) => { if (e.key === 'Shift') window._shiftHeld = false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
  }, []);

  // ESC clears any selection and exits bulk-select mode entirely.
  useEffect(() => {
    if (!bulkActive) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setSelectedIds(new Set());
        onSelectionChange?.(new Set(), [], { customStatuses, projects });
        setBulkSelectMode(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [bulkActive, onSelectionChange, customStatuses, projects]);

  // Auto-trim selection when filteredTasks changes (e.g. filter applied)
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visibleSet = new Set(filteredTasks.map(t => t.id));
    const clipped = new Set([...selectedIds].filter(id => visibleSet.has(id)));
    if (clipped.size !== selectedIds.size) {
      setSelectedIds(clipped);
      onSelectionChange?.(clipped, filteredTasks.filter(t => clipped.has(t.id)), { customStatuses, projects });
    }
  }, [filteredTasks]); // eslint-disable-line

  const handleAddTask = () => {
    openTaskCreation({
      projectId: prefillProjectId,
      sectionId: prefillSectionId,
      teamId,
      ...(prefillDueDate && { date: prefillDueDate }),
    });
  };

  const addGroup = () => {
    if (filterableFields.length === 0) return;
    const first = filterableFields[0];
    const ops = FIELD_OPERATORS[first.type] ?? FIELD_OPERATORS.select;
    setFilterGroups(prev => [...prev, {
      id: crypto.randomUUID(),
      conditions: [{ id: crypto.randomUUID(), field: first.key, operator: ops[0], value: '' }],
    }]);
  };

  const addOrToGroup = (groupId) => {
    if (filterableFields.length === 0) return;
    const first = filterableFields[0];
    const ops = FIELD_OPERATORS[first.type] ?? FIELD_OPERATORS.select;
    setFilterGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, conditions: [...g.conditions, { id: crypto.randomUUID(), field: first.key, operator: ops[0], value: '' }] }
        : g
    ));
  };

  const updateCondition = (groupId, condId, updated) => {
    setFilterGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, conditions: g.conditions.map(c => c.id === condId ? updated : c) }
        : g
    ));
  };

  const removeCondition = (groupId, condId) => {
    setFilterGroups(prev => {
      const next = prev.map(g =>
        g.id === groupId ? { ...g, conditions: g.conditions.filter(c => c.id !== condId) } : g
      );
      return next.filter(g => g.conditions.length > 0);
    });
  };

  const removeGroup = (groupId) => {
    setFilterGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterGroups([]);
  };

  // Quick-filter presets: one-click shortcuts that write regular filter
  // conditions into the filter panel (single source of truth). The user can
  // then inspect, tweak, or clear them like any hand-built filter.
  const FILTER_PRESETS = [
    { key: 'assigned_to_me', label: t('tasks.assigned_to_me'), groups: [[{ field: 'assignee', operator: 'contains', value: ME_VALUE }]] },
    { key: 'created_by_me',  label: t('tasks.created_by_me'),  groups: [[{ field: 'owner', operator: 'eq', value: ME_VALUE }]] },
    { key: 'my_tasks',       label: t('tasks.filters.my_tasks'),
      groups: [[{ field: 'assignee', operator: 'contains', value: ME_VALUE }, { field: 'owner', operator: 'eq', value: ME_VALUE }]] },
    { key: 'due_today',   label: t('tasks.filters.due_today'),  groups: [[{ field: 'due_date', operator: 'is_today', value: '' }]] },
    { key: 'overdue',     label: t('tasks.filters.overdue'),    groups: [[{ field: 'due_date', operator: 'is_overdue', value: '' }]] },
    { key: 'unassigned',  label: t('tasks.filters.unassigned'), groups: [[{ field: 'assignee', operator: 'is_not_set', value: '' }]] },
  ];

  // Structural fingerprint of a set of filter groups (ids stripped) so the
  // menu can show which preset the current filters correspond to. Any manual
  // tweak in the panel changes the fingerprint and un-checks the preset.
  const presetFingerprint = (groups) =>
    JSON.stringify(groups.map(conds => conds.map(c => [c.field, c.operator, c.value])));
  const activeFingerprint = presetFingerprint(filterGroups.map(g => g.conditions));
  const isPresetActive = (preset) => presetFingerprint(preset.groups) === activeFingerprint;

  const applyFilterPreset = (preset) => {
    if (isPresetActive(preset)) {
      // Clicking the checked preset toggles it off
      setFilterGroups([]);
      setShowPresetMenu(false);
      return;
    }
    setFilterGroups(preset.groups.map(conditions => ({
      id: crypto.randomUUID(),
      conditions: conditions.map(c => ({ id: crypto.randomUUID(), ...c })),
    })));
    // Deliberately do NOT open the filter panel here - it would push the task
    // list down. The checked preset in the menu shows what's active; the user
    // can open the panel anytime to inspect or tweak.
    setShowPresetMenu(false);
  };

  const hasActiveFilters = !!searchQuery || filterGroups.some(g => g.conditions.some(conditionIsActive));

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date < today) return { text: t('tasks.overdue'), className: 'text-red-600 dark:text-red-400 font-medium' };
    if (date < tomorrow) return { text: t('tasks.today_label'), className: 'text-amber-700 dark:text-amber-300 font-medium' };
    if (date < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) return { text: t('tasks.tomorrow_label'), className: 'text-foreground/80' };

    return { text: formatDate(date, false), className: 'text-muted-foreground' };
  };

  // Desktop toolbar controls: in the table's own row, or in the page bar's row (toolbarTarget).
  const toolbarControls = (
        <>
          {/* Bulk-select Toggle - first control in the toolbar. Reveals the
              checkbox column on demand so the table is not permanently
              shifted/indented when not selecting. */}
          {showBulkSelect && (
            <button
              onClick={toggleBulkSelectMode}
              title={bulkSelectMode ? t('common.done') : t('tasks.select_tasks')}
              aria-pressed={bulkSelectMode}
              className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-xs transition-colors ${bulkSelectMode ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
            >
              <CheckSquare className="w-3.5 h-3.5" />
              {selectedIds.size > 0 && <span className="text-[10px]">{selectedIds.size}</span>}
            </button>
          )}

          {/* Search */}
          {showSearch && (
            <div className="relative flex-1 min-w-[140px] max-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
              <Input
                placeholder={t('tasks.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs bg-transparent border-border"
              />
            </div>
          )}

          {/* Group By */}
          {showGrouping && (
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger
                title={t('ui_views.table.grouping')}
                className="h-8 w-auto gap-1 px-2 shadow-none border-border text-muted-foreground text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs">{t('tasks.no_grouping')}</SelectItem>
                {showProjectColumn && <SelectItem value="project" className="text-xs">{t('tasks.by_project')}</SelectItem>}
                <SelectItem value="status" className="text-xs">{t('tasks.by_status')}</SelectItem>
                <SelectItem value="priority" className="text-xs">{t('tasks.by_priority')}</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Filter Toggle - split button: funnel opens the filter panel,
              chevron opens the quick-filter preset menu (Assigned to me,
              Created by me, My tasks, Due today, Overdue, Unassigned). */}
          {showFilters && (
            <div className="relative flex items-center" ref={presetMenuRef}>
              <button
                onClick={() => {
                  const next = !showFilterPanel;
                  setShowFilterPanel(next);
                  if (next && filterGroups.length === 0) addGroup();
                }}
                title={t('ui_views.filters.filters')}
                className={`h-8 px-2.5 flex items-center gap-1.5 rounded-l-md border text-xs transition-colors ${showFilterPanel || hasActiveFilters ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
              >
                <Filter className="w-3.5 h-3.5" />
                {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
              </button>
              <button
                onClick={() => setShowPresetMenu(v => !v)}
                title={t('tasks.filters.presets')}
                aria-haspopup="menu"
                aria-expanded={showPresetMenu}
                className={`h-8 px-1 flex items-center rounded-r-md border border-l-0 text-xs transition-colors ${showPresetMenu || hasActiveFilters ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
              >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPresetMenu ? 'rotate-180' : ''}`} />
              </button>
              {showPresetMenu && (
                <div className="absolute top-full mt-1 left-0 min-w-[190px] bg-popover border border-border rounded-lg shadow-xl py-1 z-50">
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t('tasks.filters.presets')}
                  </div>
                  {FILTER_PRESETS.map(preset => {
                    const active = isPresetActive(preset);
                    return (
                      <button
                        key={preset.key}
                        onClick={() => applyFilterPreset(preset)}
                        aria-pressed={active}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm transition-colors hover:bg-accent ${active ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}
                      >
                        {preset.label}
                        {active && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                      </button>
                    );
                  })}
                  {hasActiveFilters && (
                    <>
                      <div className="border-t border-border my-1" />
                      <button
                        onClick={() => { clearFilters(); setShowPresetMenu(false); }}
                        className="w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:text-red-400 hover:bg-accent transition-colors"
                      >
                        {t('common.clear_all')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Column Selector */}
          {showColumnSelector && (
            <ColumnSelector
              columns={allColumnDefs}
              visibleColumns={visibleColumns}
              onToggleColumn={handleToggleColumn}
            />
          )}

          {/* Show Completed Toggle */}
          {showCompletedToggle && (
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              title={showCompleted ? t('tasks.hide_completed') : t('tasks.show_completed')}
              className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-xs transition-colors ${showCompleted ? 'text-green-400 border-green-500/40 bg-green-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
            >
              {showCompleted ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {completedCount > 0 && <span className="text-[10px]">{completedCount}</span>}
            </button>
          )}
        </>
  );

  const mobileFilterButton = (
    <button
      type="button"
      onClick={() => setShowMobileFilters(true)}
      aria-label={t('ui_views.table.filters_and_sort')}
      className={`h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${hasActiveFilters || groupBy !== 'none' ? 'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400' : 'border-border text-muted-foreground'}`}
    >
      <Filter className="w-4 h-4" />
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Mobile toolbar — compact iOS-style search row + a single filter icon.
          All advanced controls live in a sheet to avoid the SE-width wrap. A page
          with its own filter bar (toolbarTarget) gets the icon in that bar instead. */}
      {!compactMobile && toolbarTarget === undefined && (showSearch || showFilters || showGrouping) && (
        <div className="flex md:hidden items-center gap-2">
          {showSearch && (
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
              <Input
                placeholder={t('tasks.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 text-[13px] rounded-lg bg-transparent border-border"
              />
            </div>
          )}
          {mobileFilterButton}
        </div>
      )}

      {/* Standalone bulk-select toggle for tables that hide the full toolbar
          (e.g. Agenda / Home lists). Without this the checkbox column would be
          unreachable on those surfaces. Desktop-only, like bulk-select itself. */}
      {toolbarTarget === undefined && showBulkSelect && !(showSearch || showFilters || showGrouping || showAddButton) && (
        <div className="hidden md:flex items-center justify-start">
          {toolbarShown && <button
            onClick={toggleBulkSelectMode}
            title={bulkSelectMode ? t('common.done') : t('tasks.select_tasks')}
            aria-pressed={bulkSelectMode}
            className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-xs transition-colors ${bulkSelectMode ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
          >
            <CheckSquare className="w-3.5 h-3.5" />
            <span>{bulkSelectMode ? t('common.done') : t('tasks.select_tasks')}</span>
            {selectedIds.size > 0 && <span className="text-[10px] opacity-70">({selectedIds.size})</span>}
          </button>}
        </div>
      )}

      {/* Toolbar — when rendered inline (inside a project section), hide on mobile
          to keep each section visually light. Top-level tables (e.g. /app/tasks)
          keep the toolbar at all sizes. */}
      {toolbarTarget !== undefined ? (
        toolbarTarget && createPortal(
          <>
            <div className="hidden md:flex items-center gap-1.5">{toolbarShown && toolbarControls}</div>
            {/* The page's bar already filters: here only sort and grouping, in the bar's own button style. */}
            {!compactMobile && (
              <button
                type="button"
                onClick={() => setShowMobileFilters(true)}
                aria-label={t('ui_views.table.sort_and_group')}
                className="md:hidden ml-1.5 h-9 w-9 flex items-center justify-center rounded-md border border-border text-muted-foreground"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            )}
          </>,
          toolbarTarget,
        )
      ) : (showSearch || showFilters || showGrouping || showAddButton) && (
        <div className="hidden md:flex items-center gap-1.5">
          {toolbarShown && toolbarControls}

          <div className="flex-1" />

          {/* Add Task Button — desktop toolbar (mobile gets its own button below) */}
          {toolbarShown && showAddButton && (
            <button
              onClick={handleAddTask}
              className="hidden md:flex h-8 px-3 items-center gap-1.5 rounded-md bg-amber-500 text-black text-xs font-medium hover:bg-amber-400 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('tasks.add_task')}
            </button>
          )}
        </div>
      )}

      {/* Mobile action row — the desktop toolbar is hidden on mobile, so this
          single row carries the Add Task button plus a compact filter button.
          It reuses the Add Task row's vertical space (one row total) so the
          task list never moves down. Filters open in a bottom sheet. */}
      {(showAddButton || (showFilters && compactMobile)) && (
        <div className="flex md:hidden w-full -mt-2 -mb-2 items-stretch gap-1.5">
          {showAddButton && (
            <button
              onClick={handleAddTask}
              className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-md bg-amber-500 text-black text-xs font-medium hover:bg-amber-400 active:bg-amber-400 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('tasks.add_task')}
            </button>
          )}
          {/* Filter access for compact tables (project lists/sections) that
              don't render the mobile search toolbar above. Tables with the
              toolbar already have the round funnel there - no duplicate. */}
          {showFilters && compactMobile && (
            <button
              onClick={() => setShowMobileFilters(true)}
              title={t('tasks.filters.presets')}
              className={`h-8 px-3 flex items-center gap-1.5 rounded-md border text-xs transition-colors ${hasActiveFilters || groupBy !== 'none' ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border active:bg-accent'}`}
            >
              <Filter className="w-3.5 h-3.5" />
              {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
            </button>
          )}
        </div>
      )}

      {/* Mobile filter bottom sheet — overlay, so the task list never shifts */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-popover border-t border-border rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('tasks.filters.presets')}
              </span>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-1.5 -mr-1.5 text-muted-foreground active:text-foreground rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-2 pb-2 max-h-[70vh] overflow-y-auto">
              {FILTER_PRESETS.map(preset => {
                const active = isPresetActive(preset);
                return (
                  <button
                    key={preset.key}
                    onClick={() => { applyFilterPreset(preset); setShowMobileFilters(false); }}
                    aria-pressed={active}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors active:bg-accent ${active ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}
                  >
                    {preset.label}
                    {active && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                );
              })}
              {hasActiveFilters && (
                <button
                  onClick={() => { clearFilters(); setShowMobileFilters(false); }}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-muted-foreground active:bg-accent"
                >
                  {t('common.clear_all')}
                </button>
              )}

              {/* Custom filter - the same grouped (A OR B) AND (C OR D) builder
                  as the desktop panel, sharing state + persistence */}
              {showFilters && (
                <>
                  <div className="border-t border-border my-1" />
                  <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t('tasks.filters.custom')}
                  </div>
                  <div className="px-3 pb-2 space-y-2">
                    {filterGroups.map((group, idx) => (
                      <React.Fragment key={group.id}>
                        {idx > 0 && (
                          <div className="flex items-center gap-2 py-1">
                            <div className="h-px flex-1 bg-muted" />
                            <span className="text-xs text-muted-foreground font-medium tracking-wider">{t('ui_views.filters.and')}</span>
                            <div className="h-px flex-1 bg-muted" />
                          </div>
                        )}
                        <FilterGroupRow
                          group={group}
                          filterableFields={filterableFields}
                          onChangeCondition={(condId, updated) => updateCondition(group.id, condId, updated)}
                          onAddOr={() => addOrToGroup(group.id)}
                          onRemoveCondition={(condId) => removeCondition(group.id, condId)}
                          onRemoveGroup={() => removeGroup(group.id)}
                        />
                      </React.Fragment>
                    ))}
                    <button
                      onClick={addGroup}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground active:text-amber-400 transition-colors px-1.5 py-1 rounded border border-dashed border-border"
                    >
                      <Plus className="w-3 h-3" />
                      {t('ui_views.filters.and')}
                    </button>
                  </div>
                </>
              )}

              {/* Group by - same options as the desktop toolbar select */}
              {showGrouping && (
                <>
                  <div className="border-t border-border my-1" />
                  <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t('tasks.grouping.group_by')}
                  </div>
                  <div className="flex flex-wrap gap-1.5 px-3 pb-1">
                    {[
                      { value: 'none', label: t('tasks.no_grouping') },
                      ...(showProjectColumn ? [{ value: 'project', label: t('tasks.by_project') }] : []),
                      { value: 'status', label: t('tasks.by_status') },
                      { value: 'priority', label: t('tasks.by_priority') },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setGroupBy(opt.value)}
                        aria-pressed={groupBy === opt.value}
                        className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${groupBy === opt.value ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-foreground/80 border-border active:bg-accent'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Show completed toggle */}
              {showCompletedToggle && (
                <>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => setShowCompleted(!showCompleted)}
                    aria-pressed={showCompleted}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors active:bg-accent ${showCompleted ? 'text-green-400' : 'text-foreground'}`}
                  >
                    {t('tasks.show_completed')}
                    {showCompleted && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters Panel - (A OR B) AND (C OR D) */}
      {showFilterPanel && (
        <div className="p-4 bg-accent/60 rounded-xl border border-border space-y-2">
          {/* AND groups */}
          {filterGroups.map((group, idx) => (
            <React.Fragment key={group.id}>
              {idx > 0 && (
                <div className="flex items-center gap-2 py-1">
                  <div className="h-px flex-1 bg-muted" />
                  <span className="text-xs text-muted-foreground font-medium tracking-wider">{t('ui_views.filters.and')}</span>
                  <div className="h-px flex-1 bg-muted" />
                </div>
              )}
              <FilterGroupRow
                group={group}
                filterableFields={filterableFields}
                onChangeCondition={(condId, updated) => updateCondition(group.id, condId, updated)}
                onAddOr={() => addOrToGroup(group.id)}
                onRemoveCondition={(condId) => removeCondition(group.id, condId)}
                onRemoveGroup={() => removeGroup(group.id)}
              />
            </React.Fragment>
          ))}

          {/* Footer: + AND + Clear all */}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={addGroup}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-amber-700 dark:hover:text-amber-400 transition-colors"
            >
              <Plus className="w-3 h-3" />
              {t('ui_views.filters.and')}
            </button>
            {filterGroups.length > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-muted-foreground hover:text-red-400 transition-colors"
              >
                {t('common.clear_all')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tasks Table */}
      <div className={frameless ? 'overflow-hidden' : 'bg-white dark:bg-white/5 rounded-xl border border-border overflow-hidden'}>
        <div className="sm:overflow-x-auto">
        <div style={{ minWidth: window.innerWidth >= 640 && totalVisibleSpans > 12 ? `${totalVisibleSpans * 65 + (bulkActive ? 40 : 0)}px` : '100%' }}>
        {/* Table Header */}
        {wide && <div
          className="group hidden sm:grid gap-3 px-3 py-1.5 bg-muted dark:bg-white/[0.03] border-b border-border dark:border-border text-[11px] text-muted-foreground/60 uppercase tracking-wider"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {bulkActive && (() => {
            const allVisibleIds = filteredTasks.map(t => t.id);
            const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id));
            const someSelected = !allSelected && allVisibleIds.some(id => selectedIds.has(id));
            const selectionActive = selectedIds.size > 0;
            return (
              <div
                className={`flex items-center justify-center transition-opacity ${selectionActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={allSelected}
                  {...(someSelected ? { 'data-state': 'indeterminate' } : {})}
                  onCheckedChange={(checked) => {
                    const next = checked ? new Set(allVisibleIds) : new Set();
                    setSelectedIds(next);
                    onSelectionChange?.(next, filteredTasks.filter(t => next.has(t.id)), { customStatuses, projects });
                  }}
                  className="border-input data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                />
              </div>
            );
          })()}
          {columnOrder
            .filter(key => visibleColumns[key] !== false)
            .map(key => {
              const col = allColumnDefs.find(c => c.key === key);
              if (!col) return null;
              return (
                <div
                  key={key}
                  style={{ gridColumn: `span ${col.span}` }}
                  className={`relative flex items-center group/col transition-opacity ${dragOverCol === key ? 'opacity-40' : ''}`}
                  onDragOver={(e) => handleColDragOver(e, key)}
                  onDrop={(e) => handleColDrop(e, key)}
                >
                  <button
                    draggable
                    onDragStart={() => handleColDragStart(key)}
                    onDragEnd={handleColDragEnd}
                    onClick={(e) => e.stopPropagation()}
                    title={t('ui_views.table.drag_to_reorder')}
                    className="absolute -left-3.5 cursor-grab active:cursor-grabbing opacity-0 group-hover/col:opacity-50 hover:!opacity-100 transition-opacity"
                  >
                    <GripVertical className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleSort(key)}
                    className="flex items-center hover:text-foreground/80 transition-colors text-left"
                  >
                    {col.label}
                    {col.fieldDef?.scope === 'project' && (
                      <Pin className="w-3 h-3 text-muted-foreground flex-shrink-0 ml-1" title={t('ui_views.table.project_field')} />
                    )}
                    {getSortIcon(key)}
                  </button>
                  {/* Edit/Delete menu for custom field columns */}
                  {col.isCustom && !readOnly && (
                    <div className="relative ml-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFieldMenuOpen(prev => prev === key ? null : key);
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.nativeEvent.stopPropagation();
                        }}
                        className="opacity-0 group-hover/col:opacity-100 flex items-center justify-center w-4 h-4 rounded text-muted-foreground/70 hover:text-foreground/80 hover:bg-accent transition-all"
                        title={t('ui_views.table.field_options')}
                      >
                        <MoreHorizontal className="w-3 h-3" />
                      </button>
                      {fieldMenuOpen === key && (
                        <div
                          className="absolute top-full right-0 mt-1 z-50 bg-popover border border-border rounded-lg shadow-xl py-1 min-w-[140px]"
                          onClick={e => e.stopPropagation()}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopPropagation();
                          }}
                        >
                          <button
                            onClick={() => {
                              setEditingFieldDef(col.fieldDef);
                              setFieldMenuOpen(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-accent/60 hover:text-foreground transition-colors"
                          >
                            <Pencil className="w-3 h-3" /> {t('tasks.edit_field')}
                          </button>
                          <button
                            onClick={() => {
                              setConfirmDeleteField({ id: col.fieldDef.id, label: col.label });
                              setFieldMenuOpen(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" /> {t('tasks.delete_field')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          {/* "+" button to add a new custom field column */}
          {!readOnly && limitOf('custom_fields_per_project') !== 0 && (
            <div className="flex items-center justify-center">
              <button
                onClick={() => setShowAddFieldModal(true)}
                title={t('tasks.add_field')}
                className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground/70 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-500/10 transition-all"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>}

        {/* Groups */}
        {(Object.entries(groupedTasks).length === 0 || filteredTasks.length === 0) && emptyInlineAdd && showInlineAdd ? (
          <InlineTaskRow
            projectId={prefillProjectId}
            sectionId={prefillSectionId}
            teamId={teamId}
            dueDate={prefillDueDate}
            onTaskCreated={handleInlineTaskCreated}
            gridTemplate={gridTemplate}
            columnOrder={columnOrder}
            visibleColumns={visibleColumns}
            customStatuses={customStatuses}
            projects={projects}
            labels={labels}
            taskTypes={taskTypes}
            leadingCell={bulkActive}
          />
        ) : Object.entries(groupedTasks).length === 0 || filteredTasks.length === 0 ? (
          <div className="text-center py-16">
            <Folder className="w-12 h-12 mx-auto mb-4 text-muted-foreground/70" />
            <h3 className="text-lg font-medium text-foreground mb-2">{emptyMessage ?? t('tasks.no_tasks_found')}</h3>
            <p className="text-muted-foreground mb-4">{emptyDescription ?? t('ui_views.table.empty_description')}</p>
            {showAddButton && (
              <Button
                onClick={handleAddTask}
                variant="outline"
                className="border-border text-foreground/80 hover:text-foreground"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('tasks.add_task')}
              </Button>
            )}
          </div>
        ) : (
          Object.entries(groupedTasks).map(([groupKey, group]) => {
            const isExpanded = expanded[groupKey] !== false;
            const groupTasks = group.tasks || group;
            const groupLabel = group.label || groupKey;
            const project = group.project;

            return (
              <div key={groupKey} className="border-b border-border dark:border-border last:border-b-0">
                {/* Group Header */}
                {groupBy !== 'none' && (
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted dark:hover:bg-white/5 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}

                    {groupBy === 'project' && project ? (
                      <ProjectChip
                        project={project}
                        dotSize="w-3 h-3"
                        textClass="font-semibold text-foreground"
                      />
                    ) : null}
                    {groupBy === 'status' && (() => {
                      const statusKey = groupKey.replace('status:', '');
                      const customS = customStatuses.find(s => s.id === statusKey);
                      return customS
                        ? <div className="w-3 h-3 rounded-full" style={{ backgroundColor: customS.color }} />
                        : <div className={`w-3 h-3 rounded-full ${statusConfig[statusKey]?.color || 'bg-gray-500'}`} />;
                    })()}
                    {groupBy === 'priority' && (
                      <div className={`w-3 h-3 rounded-full ${priorityConfig[groupKey.replace('priority:', '')]?.color || 'bg-gray-500'}`} />
                    )}

                    {/* For project grouping the ProjectChip already renders
                        the label (team / name). Fall back to groupLabel only
                        when there's no project (e.g. "No project" group) or
                        when grouping by status / priority. */}
                    {(groupBy !== 'project' || !project) && (
                      <span className="font-semibold text-foreground">{groupLabel}</span>
                    )}
                    <span className="text-sm text-muted-foreground tabular-nums">{groupTasks.length}</span>
                  </button>
                )}

                {/* Group Tasks */}
                {isExpanded && (
                  <div className="divide-y divide-border dark:divide-border/50">
                    {groupRows[groupKey].map(({ task, depth, childCount }, rowIndex, rows) => {
                      const flatIndex = displayTasks.indexOf(task);
                      const nest = {
                        depth,
                        childCount,
                        parentTitle: task.parent_id && depth === 0 ? taskTitles.get(task.parent_id) : null,
                      };
                      const isSelected = selectedIds.has(task.id);
                      const priority = priorityConfig[task.priority] || priorityConfig.p4;
                      const status = statusConfig[task.status] || statusConfig.todo;
                      const dueInfo = formatDueDate(task.due_date);
                      const taskProject = getProjectById(task.project_id);

                      const isCompleted = isTaskCompleted(task);

                      return (
                        <React.Fragment key={task.id}>
                        <div
                          draggable={!readOnly && editingTitleId !== task.id}
                          onDragStart={(e) => {
                            if (e.target.closest('button, input, textarea, select, a')) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData('text/plain', task.id);
                            e.dataTransfer.effectAllowed = 'move';
                            e.currentTarget.style.opacity = '0.4';
                            draggingTaskId = task.id;
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.opacity = '';
                            draggingTaskId = null;
                          }}
                          data-task-row
                          onDragOver={(e) => handleRowDragOver(e, rows, rowIndex)}
                          onDrop={(e) => handleRowDrop(e, rows)}
                          onClick={() => handleTaskClick(task)}
                          className={`text-sm hover:bg-muted dark:hover:bg-white/[0.03] transition-colors ${readOnly ? '' : 'cursor-pointer'} group relative ${isSelected ? 'bg-amber-500/5' : ''} ${recentlyCreatedIds.has(task.id) ? 'animate-inline-task-flash' : ''} ${dropTarget?.taskId === task.id && dropTarget.zone === 'into' ? 'bg-amber-500/10 ring-1 ring-inset ring-amber-500/50' : ''}`}
                        >
                          {/* Where a dragged task lands: the line starts at the level it will have. */}
                          {dropTarget?.taskId === task.id && dropTarget.zone !== 'into' && (
                            <div
                              className={`absolute right-0 h-0.5 rounded-full bg-amber-500 pointer-events-none z-10 ${dropTarget.zone === 'before' ? '-top-px' : '-bottom-px'}`}
                              style={{ left: 12 + depth * 24 }}
                            />
                          )}
                          {/* Drag handle - hidden on mobile, shows on desktop hover.
                              Sized to fit entirely inside the row's 12px left gutter
                              (px-3) so it never gets clipped behind the complete
                              checkbox that starts the first column. */}
                          {!readOnly && wide && (
                            <div className="absolute left-0 inset-y-0 w-3 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                              <GripVertical className="w-2.5 h-2.5 text-muted-foreground/50" />
                            </div>
                          )}
                          {/* Mobile card — iOS-style row: priority bar | checkbox | title + subtitle | chevron */}
                          {!wide && <div
                            className={`relative flex items-center gap-3 py-3 sm:hidden ${groupBy !== 'none' ? 'pl-10 pr-3' : 'px-3'}`}
                            style={depth ? { paddingLeft: (groupBy !== 'none' ? 40 : 12) + depth * 24 } : undefined}
                          >
                            <button onClick={(e) => handleToggleComplete(e, task)} disabled={readOnly} className="flex-shrink-0 -m-2 p-2" aria-label={t('ui_views.table.toggle_complete')}>
                              {isCompleted
                                ? <CheckCircle2 className="w-[22px] h-[22px] text-green-500" />
                                : <Circle className={`w-[22px] h-[22px] ${circleTone(task.priority)}`} />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className={`text-[15px] leading-snug truncate block ${isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{task.title}</span>
                                <SpotlightMark taskId={task.id} mark={spotlight.items[task.id]} sections={spotlight.sections} />
                              </div>
                              {nest.parentTitle && (
                                <span className="text-[12px] text-muted-foreground truncate block">{nest.parentTitle}</span>
                              )}
                              {(() => {
                                // Render an iOS-style subtitle: only the *meaningful* meta. Default "To Do"
                                // status is implicit from the empty circle, so we skip it.
                                let statusLabel = null;
                                let statusStyle;
                                let statusClass = 'text-muted-foreground';
                                if (task.status_id && customStatuses.length > 0) {
                                  const cs = customStatuses.find(s => s.id === task.status_id);
                                  // A status not started yet is the empty circle already: only the others are named.
                                  if (cs && cs.category !== 'not_started') { statusLabel = statusName(cs, t); statusStyle = { color: cs.color }; }
                                } else if (task.status && task.status !== 'todo') {
                                  statusLabel = legacyStatusName(task.status);
                                  statusClass = status.textColor;
                                }
                                const dateLabel = dueInfo?.text;
                                const dateClass = dueInfo?.className?.includes('red')
                                  ? 'text-red-500'
                                  : dueInfo?.className?.includes('amber') || dueInfo?.className?.includes('yellow')
                                    ? 'text-amber-500'
                                    : 'text-muted-foreground';
                                // The project too, unless the rows are already grouped by it (the design's
                                // "● Lumen Co · Today").
                                const showProject = taskProject && groupBy !== 'project' && !prefillProjectId;
                                const parts = [
                                  showProject && (
                                    <span key="p" className="min-w-0 inline-flex items-center gap-1.5 text-muted-foreground">
                                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: taskProject.color || '#3B82F6' }} />
                                      <span className="truncate">{taskProject.name}</span>
                                    </span>
                                  ),
                                  dateLabel && <span key="d" className={`flex-shrink-0 ${dateClass}`}>{dateLabel}</span>,
                                  statusLabel && <span key="s" className={`flex-shrink-0 ${statusClass}`} style={statusStyle}>{statusLabel}</span>,
                                ].filter(Boolean);
                                if (!parts.length) return null;
                                return (
                                  <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] min-w-0">
                                    {parts.map((part, i) => (
                                      <React.Fragment key={part.key}>
                                        {i > 0 && <span className="text-muted-foreground/40 flex-shrink-0">·</span>}
                                        {part}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>}
                          {/* Desktop grid */}
                          {wide && <div
                            className={`hidden sm:grid gap-3 py-1.5 items-center ${groupBy !== 'none' ? 'pl-10 pr-3' : 'px-3'}`}
                            style={{ gridTemplateColumns: gridTemplate }}
                          >
                            {bulkActive && (
                              <div
                                className="flex items-center justify-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => handleCheckboxChange(task.id, flatIndex, checked)}
                                  className="border-input data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                />
                              </div>
                            )}
                            {columnOrder
                              .filter(key => visibleColumns[key] !== false)
                              .map(key => renderTaskCell(key, task, priority, status, dueInfo, taskProject, nest))
                            }
                          </div>}
                        </div>
                        </React.Fragment>
                      );
                    })}
                    {/* Inline quick-add row - rendered as a grid row matching
                        the surrounding tasks (same height/font/columns) so it
                        reads as a placeholder for the next task. */}
                    {showInlineAdd && (
                      <InlineTaskRow
                        projectId={prefillProjectId || (groupBy === 'project' ? (project?.id || null) : null)}
                        sectionId={prefillSectionId}
                        statusId={groupBy === 'status' ? groupKey.replace('status:', '') : null}
                        teamId={teamId}
                        dueDate={prefillDueDate}
                        onTaskCreated={handleInlineTaskCreated}
                        gridTemplate={gridTemplate}
                        columnOrder={columnOrder}
                        visibleColumns={visibleColumns}
                        customStatuses={customStatuses}
                        projects={projects}
                        labels={labels}
                        taskTypes={taskTypes}
                        indented={groupBy !== 'none'}
                        leadingCell={bulkActive}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Table Footer with Aggregations */}
        {wide && filteredTasks.length > 0 && (
          <TableFooter
            tasks={filteredTasks}
            columns={allColumnDefs}
            visibleColumns={visibleColumns}
            footerConfig={footerConfig}
            onFooterConfigChange={handleFooterConfigChange}
            groupBy={groupBy}
          />
        )}
        </div>{/* end min-width */}
        </div>{/* end overflow-x-auto */}
      </div>

      {/* Inline Cell Editor */}
      {editingCell && (
        <div
          ref={inlineDropdownRef}
          className="fixed bg-white dark:bg-popover border border-border dark:border-border rounded-xl shadow-2xl z-50"
          style={editingCell.dropdownStyle}
        >
          {editingCell.columnKey === 'status' && (
            customStatuses.length > 0
              // On aggregated tables customStatuses spans every team, so the
              // picker must offer only the row's own team statuses.
              ? customStatuses.filter(s => s.category !== 'archived' && (s.team_id ?? null) === (editingCell.task.team_id ?? null)).map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleCellUpdate(editingCell.taskId, 'status_id', s.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                      editingCell.task.status_id === s.id
                        ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                        : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    {statusName(s, t)}
                  </button>
                ))
              : Object.entries(statusConfig).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => handleCellUpdate(editingCell.taskId, 'status', key)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                      editingCell.task.status === key
                        ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                        : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.color}`} />
                    {statusValueName(key, t)}
                  </button>
                ))
          )}

          {editingCell.columnKey === 'priority' && Object.entries(priorityConfig).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => handleCellUpdate(editingCell.taskId, 'priority', key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                editingCell.task.priority === key
                  ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                  : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
              }`}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.color}`} />
              {t(cfg.altLabelKey)}
            </button>
          ))}

          {editingCell.columnKey === 'duration' && (
            <div className="p-3 w-60">
              <h4 className="font-medium text-foreground dark:text-foreground mb-3 text-sm">{t('tasks.time_estimation')}</h4>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[15, 30, 45, 60, 90, 120].map(mins => (
                  <button
                    key={mins}
                    onClick={() => handleCellUpdate(editingCell.taskId, 'duration', mins)}
                    className={`px-2 py-1.5 rounded-lg text-sm transition-colors ${editingCell.task.duration === mins ? 'bg-amber-500 text-black font-medium' : 'bg-muted dark:bg-white/5 text-foreground/80 hover:bg-accent dark:hover:bg-white/10'}`}
                  >
                    {mins < 60 ? t('ui_task.duration.minutes', { m: mins }) : t('ui_task.duration.hours', { h: mins / 60 })}
                  </button>
                ))}
              </div>
              <div className="border-t border-border dark:border-border pt-3">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="5"
                    step="5"
                    defaultValue={editingCell.task.duration || 60}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleCellUpdate(editingCell.taskId, 'duration', parseInt(e.currentTarget.value) || null); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingCell(null); }
                    }}
                    className="flex-1 min-w-0 w-full px-3 py-2 bg-muted dark:bg-white/5 border border-border dark:border-border rounded-lg text-sm text-foreground dark:text-foreground outline-none focus:border-amber-500/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="text-muted-foreground dark:text-muted-foreground text-sm">{t('common.min')}</span>
                </div>
              </div>
            </div>
          )}

          {editingCell.columnKey === 'project' && (
            // The app's one project picker: a search, Favorites, personal projects, then each team.
            <div className="w-72 flex flex-col">
              <ProjectList compact autoFocus projects={projects.filter((p) => !p.deleted_at)} projectId={editingCell.task.project_id}
                noneLabel={t('tasks.no_project')} onPick={(id) => handleCellUpdate(editingCell.taskId, 'project_id', id)} />
            </div>
          )}
          {editingCell.columnKey === 'assignee' && (() => {
            const effectiveTeamId = editingCell.task.team_id
              || projects.find(p => p.id === editingCell.task.project_id)?.team_id;
            const team = effectiveTeamId ? teamsMap[effectiveTeamId] : null;
            // You can always assign yourself, also in a personal project; teammates follow.
            const members = [
              ...(user ? [{ user_id: user.id, full_name: user.full_name, email: user.email, avatar_url: user.avatar_url, isSelf: true }] : []),
              ...(team?.members || []).filter((m) => m.user_id !== user?.id),
            ];
            const currentAssignees = editingCell.task.assigned_to || [];
            const toggleAssignee = (userId) => {
              const next = currentAssignees.includes(userId)
                ? currentAssignees.filter(id => id !== userId)
                : [...currentAssignees, userId];
              handleDateFieldUpdate(editingCell.taskId, 'assigned_to', next);
            };
            const scopeBots = Object.values(botsById).filter((b) => (effectiveTeamId
              ? b.owner_type === 'team' && b.owner_id === effectiveTeamId
              : b.owner_type === 'user' && b.owner_id === editingCell.task.user_id));
            const toggleBot = (botId) => {
              const next = editingCell.task.assigned_bot_id === botId ? null : botId;
              assignTaskBot.mutate({ taskId: editingCell.taskId, botId: next });
              setEditingCell((c) => c && ({ ...c, task: { ...c.task, assigned_bot_id: next } }));
            };
            return (
              <div className="py-1 max-h-64 overflow-y-auto">
                {members.length === 0 ? (scopeBots.length > 0 ? null : (
                  <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                    {t('tasks.select_project_with_team') || 'Select a project with a team to assign members'}
                  </div>
                )) : (
                  members.map(m => {
                    const isSelected = currentAssignees.includes(m.user_id);
                    return (
                      <button
                        key={m.user_id}
                        onClick={() => toggleAssignee(m.user_id)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
                          isSelected ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground' : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {m.avatar_url ? (
                            <img src={m.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-indigo-400 dark:bg-indigo-600 flex items-center justify-center text-[10px] text-white font-medium shrink-0">
                              {m.full_name?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <span className="truncate">{m.isSelf ? t('tasks.me') : (m.full_name || m.email)}</span>
                        </div>
                        {isSelected && <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />}
                      </button>
                    );
                  })
                )}
                {scopeBots.length > 0 && (
                  <>
                    <div className="my-1 border-t border-border dark:border-white/10" />
                    <div className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('bots.sidebar_title')}
                    </div>
                    {scopeBots.map((b) => {
                      const isSelected = editingCell.task.assigned_bot_id === b.id;
                      return (
                        <button
                          key={b.id}
                          onClick={() => toggleBot(b.id)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
                            isSelected ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground' : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <BotAvatar bot={b} size={20} />
                            <BotName bot={b} />
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })()}
          {editingCell.columnKey === 'team' && (() => {
            const effectiveTeamId = editingCell.task.team_id
              || projects.find(p => p.id === editingCell.task.project_id)?.team_id;
            return (
              <div className="py-1 max-h-52 overflow-y-auto">
                <button
                  onClick={() => handleCellUpdate(editingCell.taskId, 'team_id', null)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                    !effectiveTeamId ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium' : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                  }`}
                >
                  <span className="w-2 h-2 rounded bg-gray-400 dark:bg-accent shrink-0" />
                  {t('tasks.no_team')}
                </button>
                {Object.values(teamsMap).map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleCellUpdate(editingCell.taskId, 'team_id', t.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                      effectiveTeamId === t.id ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium' : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                    }`}
                  >
                    <span className="w-2 h-2 rounded bg-amber-500 shrink-0" />
                    {t.name}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Delete Custom Field Confirm */}
      <ConfirmModal
        isOpen={!!confirmDeleteField}
        onClose={() => setConfirmDeleteField(null)}
        onConfirm={() => handleDeleteField(confirmDeleteField?.id)}
        title={t('ui_views.table.delete_field_title', { name: confirmDeleteField?.label })}
        message={t('tasks.delete_field_confirm_message')}
        confirmLabel={t('common.delete')}
        danger
      />

      {/* Add / Edit Custom Field Modal */}
      {(showAddFieldModal || editingFieldDef) && (
        <AddFieldModal
          onClose={() => { setShowAddFieldModal(false); setEditingFieldDef(null); }}
          onFieldCreated={handleFieldCreated}
          onFieldUpdated={handleFieldUpdated}
          initialDef={editingFieldDef}
          projectId={projectId}
        />
      )}
    </div>
  );
};

// Pages draw again on every refresh of their queries; a table whose props did not change
// (Home has three) is not drawn again with them.
export default React.memo(TasksTable);
